<template>
	<div class="western-container">
		<div class="main-layout">
			<!-- 左侧药品剂型树形菜单 -->
			<div class="left-sidebar">
				<div class="tree-header">
					<div class="tree-search">
						<el-input
							v-model="searchForm"
							placeholder="搜索剂型"
							size="mini"
							clearable
							prefix-icon="el-icon-Ssearch"
							@input="handleSearchForm"
						/>
					</div>
				</div>
				<div 
					class="tree-content"
					ref="treeContent"
					@scroll="handleTreeScroll"
				>
					<el-tree
						:data="treeData"
						:props="treeProps"
						:highlight-current="true"
						:expand-on-click-node="false"
						node-key="id"
						:default-expanded-keys="['all']"
						@node-click="handleNodeClick"
						v-loading="treeLoading"
						element-loading-text="加载中..."
						element-loading-spinner="el-icon-loading"
						element-loading-background="rgba(255, 255, 255, 0.7)"
					>
						<div slot-scope="{ node, data }" class="tree-node">
							<span>{{ node.label }}</span>
							<span v-if="data.count" class="tree-count">({{ data.count }})</span>
						</div>
					</el-tree>
				</div>
			</div>

			<!-- 右侧主内容区 -->
			<div class="right-content">
				<!-- 顶部操作栏 -->
				<div class="operation-bar">
					<div class="search-container">
						<el-input
							v-model="searchKeyword"
							placeholder="药品编码/药品名称"
							class="search-input"
							size="mini"
							clearable
							@keyup.enter="handleSearch"
						>
							<el-button slot="append" icon="el-icon-Ssearch" @click="handleSearch">搜索</el-button>
						</el-input>
					</div>
					<div class="button-group">
						<!-- 新增药品按钮 -->
						<el-button 
							type="primary" 
							size="mini" 
							icon="el-icon-plus"
							style="background-color: #13c2c2; border-color: #13c2c2;"
							@click="openAddDialog"
							v-if="$store.getters.hasPermission('system:info:add')"
						>
							新增药品
						</el-button>
						<el-button 
							size="mini" 
							icon="el-icon-download" 
							style="background-color: #ff6700; color: #fff;"
							@click="handleCloudDownload"
						>
							导入模板下载
						</el-button>
						<el-button 
							size="mini" 
							icon="el-icon-upload2" 
							type="primary" 
							plain
							@click="handleImport"
						>
							导入
						</el-button>
						<el-button 
							size="mini" 
							icon="el-icon-download" 
							type="success" 
							plain
							@click="handleExport"
						>
							导出
						</el-button>
					</div>
				</div>

				<!-- 内容区域头部 -->
				<div class="content-header">
					<div class="content-title">
						<span class="title-text">西药列表</span>
						<div class="filter-group">
							<el-dropdown
								trigger="hover"
								@command="handleTimeFilter"
								class="filter-dropdown"
							>
								<span class="el-dropdown-link filter-item">
									{{ timeFilterLabel }}<i class="el-icon-arrow-down el-icon--right"></i>
								</span>
								<el-dropdown-menu slot="dropdown">
									<el-dropdown-item command="all">全部时间</el-dropdown-item>
									<el-dropdown-item command="today">今天</el-dropdown-item>
									<el-dropdown-item command="week">一周前</el-dropdown-item>
									<el-dropdown-item command="month">一月前</el-dropdown-item>
									<el-dropdown-item command="year">一年前</el-dropdown-item>
								</el-dropdown-menu>
							</el-dropdown>
							<el-dropdown
								trigger="hover"
								@command="handleExpensiveFilter"
								class="filter-dropdown"
							>
								<span class="el-dropdown-link filter-item">
									{{ expensiveFilterLabel }}<i class="el-icon-arrow-down el-icon--right"></i>
								</span>
								<el-dropdown-menu slot="dropdown">
									<el-dropdown-item command="all">全部</el-dropdown-item>
									<el-dropdown-item command="1">普价</el-dropdown-item>
									<el-dropdown-item command="2">贵重</el-dropdown-item>
									<el-dropdown-item command="3">昂贵</el-dropdown-item>
									<el-dropdown-item command="4">免费</el-dropdown-item>
								</el-dropdown-menu>
							</el-dropdown>
						</div>
					</div>
					<div class="view-switch">
						<span class="switch-label">列表</span>
						<el-switch
							v-model="isCardView"
							active-color="#409eff"
							inactive-color="#67c23a"
							@change="handleViewSwitch"
						/>
						<span class="switch-label">卡片</span>
					</div>
				</div>

				<!-- 中间内容区域 -->
				<div class="content-area">
					<!-- 表格视图 -->
					<div v-if="!isCardView" class="table-container">
						<el-table
							ref="drugTable"
							:data="filteredDrugList"
							style="width: 100%"
							height="calc(100vh - 280px)"
							:header-cell-style="{ 
								background: '#f5f7fa', 
								color: '#333',
								fontWeight: 'bold',
								fontSize: '13px'
							}"
							:cell-style="{ padding: '6px 0' }"
							border
							stripe
							v-loading="tableLoading"
							element-loading-text="加载中..."
							element-loading-spinner="el-icon-loading"
							:row-class-name="tableRowClassName"
						>
							<!-- 展开列 - 固定在左侧 -->
							<el-table-column 
								type="expand" 
								width="50" 
								fixed="left"
							>
								<template slot="header">
									<el-button
										type="text"
										icon="el-icon-s-tools"
										@click="handleColumnSetting"
										style="padding: 0;"
									/>
								</template>
								<template slot-scope="props">
									<div class="expanded-content">
										<h4>生产厂家详细信息</h4>
										<el-table :data="[props.row]" style="width: 100%" class="manufacturer-table"
											size="mini" border>
											<el-table-column label="生产厂家" prop="manufacturerName" width="200"></el-table-column>
											<el-table-column label="药品规格" prop="drugSpec" width="150"></el-table-column>
											<el-table-column label="包装规格" prop="pacSpec" width="150"></el-table-column>
											<el-table-column label="单位剂量" prop="unitDose" width="100"></el-table-column>
											<el-table-column label="剂量单位" prop="dosUnt" width="100"></el-table-column>
											<el-table-column label="包装数量" prop="pacCnt" width="100"></el-table-column>
										</el-table>
									</div>
								</template>
							</el-table-column>
							
							<!-- 行号列 - 固定在左侧 -->
							<el-table-column
								label="行号"
								width="60"
								align="center"
							>
								<template slot-scope="scope">
									{{ scope.$index + 1 }}
								</template>
							</el-table-column>
							
							<!-- 贵重类型列 -->
							<el-table-column
								label="贵重类型"
								prop="expensiveType"
								width="76"
								minWidth="76"
								align="center"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									<el-tag
										size="mini"
										:type="getExpensiveTypeTagType(scope.row.expensiveType)"
									>
										{{ getExpensiveTypeLabel(scope.row.expensiveType) }}
									</el-tag>
								</template>
							</el-table-column>
							
							<!-- 药典名称列 - 固定在左侧 -->
							<el-table-column
								label="药品名称"
								prop="drugName"
								width="160"
								minWidth="100"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									<el-tooltip 
										placement="top" 
										:content="getTooltipContent(scope.row)"
									>
										<span style="font-weight: bold;">{{ scope.row.drugName || '-' }}</span>
									</el-tooltip>
								</template>
							</el-table-column>
							
							<!-- 药品编码列 -->
							<el-table-column
								label="编码/条形码"
								prop="code"
								width="140"
								minWidth="120"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									<span style="font-weight: bold;font-size:12px;font-style: italic;">{{ scope.row.code || '-' }}</span>
								</template>
							</el-table-column>
							
							<!-- 别名列 -->
							<el-table-column
								label="别名"
								prop="alis"
								width="100"
								minWidth="90"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.alis || '-' }}
								</template>
							</el-table-column>
							
							<!-- 药品剂型列 -->
							<el-table-column
								label="药品剂型"
								prop="dosform"
								width="100"
								minWidth="90"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.dosform || '-' }}
								</template>
							</el-table-column>
							
							<!-- 包装规格列 -->
							<el-table-column
								label="包装规格"
								prop="pacSpec"
								width="100"
								minWidth="90"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.pacSpec || '-' }}
								</template>
							</el-table-column>
							
							<!-- 包装数量列 -->
							<el-table-column
								label="包装数量"
								prop="pacCnt"
								width="90"
								minWidth="80"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.pacCnt || '-' }}
								</template>
							</el-table-column>
							
							<!-- 包装单位列 -->
							<el-table-column
								label="包装单位"
								prop="pacUnt"
								width="100"
								minWidth="90"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.pacUnt || '-' }}
								</template>
							</el-table-column>
							
							<!-- 指定售价列 -->
							<el-table-column
								label="指定售价"
								prop="isFixPrice"
								width="100"
								minWidth="90"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.isFixPrice === 1 ? '已指定' : scope.row.isFixPrice === 0 ? '未指定' : '-' }}
								</template>
							</el-table-column>
							
							<!-- 最小指定售价列 -->
							<el-table-column
								label="最小指定售价"
								prop="fixMinSalePric"
								width="120"
								minWidth="100"
								align="center"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.fixMinSalePric ? `¥${scope.row.fixMinSalePric}` : '-' }}
								</template>
							</el-table-column>
							
							<!-- 包装指定售价列 -->
							<el-table-column
								label="包装指定售价"
								prop="fixPacSalePric"
								width="120"
								minWidth="100"
								align="center"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.fixPacSalePric ? `¥${scope.row.fixPacSalePric}` : '-' }}
								</template>
							</el-table-column>
							
							<!-- 手工划价列 -->
							<el-table-column
								label="手工划价"
								prop="manualPric"
								width="100"
								minWidth="90"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.manualPric === 1 ? '已划价' : scope.row.manualPric === 0 ? '未划价' : '-' }}
								</template>
							</el-table-column>
							
							<!-- 给药方式列 -->
							<el-table-column
								label="给药方式"
								prop="ruteName"
								width="100"
								minWidth="90"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.ruteName || '-' }}
								</template>
							</el-table-column>
							
							<!-- 单位剂量列 -->
							<el-table-column
								label="单位剂量"
								prop="unitDose"
								width="90"
								minWidth="80"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.unitDose || '-' }}
								</template>
							</el-table-column>
							
							<!-- 剂量单位列 -->
							<el-table-column
								label="剂量单位"
								prop="dosUnt"
								width="90"
								minWidth="80"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.dosUnt || '-' }}
								</template>
							</el-table-column>
							
							<!-- 生产厂家列 -->
							<el-table-column
								label="生产厂家"
								prop="manufacturerName"
								width="150"
								minWidth="120"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									{{ scope.row.manufacturerName || '-' }}
								</template>
							</el-table-column>
							
							<!-- 基本药物列 -->
							<el-table-column
								label="基本药物"
								prop="basMednB"
								width="80"
								minWidth="70"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									<el-tag
										v-if="scope.row.basMednB"
										size="small"
										:type="scope.row.basMednB === '是' ? 'success' : 'info'"
									>
										{{ scope.row.basMednB }}
									</el-tag>
									<span v-else>-</span>
								</template>
							</el-table-column>
							
							<!-- 抗生素列 -->
							<el-table-column
								label="抗生素否"
								prop="antcB"
								width="90"
								minWidth="80"
								show-overflow-tooltip
							>
								<template slot-scope="scope">
									<el-tag
										v-if="scope.row.antcB"
										size="small"
										:type="scope.row.antcB === '是' ? 'success' : 'info'"
									>
										{{ scope.row.antcB }}
									</el-tag>
									<span v-else>-</span>
								</template>
							</el-table-column>
							
							<!-- 操作列 -->
							<el-table-column
								label="操作"
								width="166"
								fixed="right"
								align="center"
							>
								<template slot-scope="scope">
									<el-button
										type="text"
										size="mini"
										@click="handleDetail(scope.row)"
									>
										详情
									</el-button>
									<el-divider direction="vertical" />
									<el-button
										type="text"
										size="mini"
										style="color: #da741b;"
										@click="handlePriceEdit(scope.row)"
										v-if="$store.getters.hasPermission('system:info:fix')"
									>
										调价
									</el-button>
									<el-button
										type="text"
										size="mini"
										style="color: #515151;"
										@click="handleEdit(scope.row)"
										v-if="$store.getters.hasPermission('system:info:edit')"
									>
										修改
									</el-button>
									<el-button
										type="text"
										size="mini"
										style="color: #aa0000;"
										@click="handleDelete(scope.row)"
									>
										删除
									</el-button>
								</template>
							</el-table-column>
						</el-table>
						
						<!-- 空状态 -->
						<div v-if="filteredDrugList.length === 0" class="empty-state">
							<el-empty description="暂无药品数据">
								<el-button type="primary" icon="el-icon-plus" size="mini" @click="openAddDialog">
									添加药品
								</el-button>
							</el-empty>
						</div>
					</div>

					<!-- 卡片视图 -->
					<div v-else class="card-container">
					  <div v-if="filteredDrugList.length > 0" class="card-list">
					    <el-row :gutter="16">
					      <el-col
					        v-for="(item, index) in filteredDrugList"
					        :key="index"
					        :xs="24"
					        :sm="12"
					        :md="8"
					        :lg="6"
					        :xl="4"
					      >
					        <!-- 使用新的卡片容器结构 -->
					        <div class="drug-card-container">
					          <!-- 指定售价标记 - 放在卡片容器内部，但在卡片内容之上 -->
					          <el-badge
					            v-if="item.isFixPrice == 1"
					            value="指定"
					            type="danger"
					            class="card-badge"
					          />
					          
					          <!-- 卡片内容 -->
					          <div 
					            class="drug-card-wrapper"
					            :class="getCardThemeClass(item.expensiveType)"
					          >
					            <el-card class="drug-card">
					              <!-- 上部分 -->
					              <div class="card-top">
					                <div class="card-image">
					                  <div class="drug-image-default">
					                    {{ getFirstChar(item.drugName) }}
					                  </div>
					                </div>
					                <div class="card-info">
					                  <el-tooltip :content="item.drugName" placement="top" :disabled="!isTextOverflow(item.drugName, 100)">
					                    <div class="drug-name">
					                      {{ item.drugName || '未知药品' }}
					                    </div>
					                  </el-tooltip>
					                  <el-tooltip :content="item.code" placement="top" :disabled="!isTextOverflow(item.code, 100)">
					                    <div class="drug-code">
					                      {{ item.code || '-' }}
					                    </div>
					                  </el-tooltip>
					                  <el-tooltip :content="item.drugSpec" placement="top" :disabled="!isTextOverflow(item.drugSpec, 100)">
					                    <div class="drug-spec">
					                      {{ item.drugSpec || '-' }}
					                    </div>
					                  </el-tooltip>
					                </div>
					              </div>
					              
					              <!-- 中部分 -->
					              <div class="card-middle">
					                <div class="price-info">
					                  <!-- 最小指定售价 -->
					                  <div class="price-item">
					                    <span class="price-label">最小指定售价：</span>
					                    <el-tooltip :content="`¥${item.fixMinSalePric || '0.00'}`" placement="top" :disabled="!isTextOverflow(`¥${item.fixMinSalePric || '0.00'}`, 60)">
					                      <span class="price-value">￥{{ item.fixMinSalePric || '0.00' }}</span>
					                    </el-tooltip>
					                  </div>
					                  <!-- 包装指定售价 -->
					                  <div class="price-item">
					                    <span class="price-label">包装指定售价：</span>
					                    <el-tooltip :content="`¥${item.fixPacSalePric || '0.00'}`" placement="top" :disabled="!isTextOverflow(`¥${item.fixPacSalePric || '0.00'}`, 60)">
					                      <span class="price-value">￥{{ item.fixPacSalePric || '0.00' }}</span>
					                    </el-tooltip>
					                  </div>
					                </div>
					                <div class="create-time">
					                  <span class="time-label">创建时间：</span>
					                  <el-tooltip :content="formatTime(item.createTime)" placement="top" :disabled="!isTextOverflow(formatTime(item.createTime), 120)">
					                    <span class="time-value">{{ formatTime(item.createTime) }}</span>
					                  </el-tooltip>
					                </div>
					              </div>
					              
					              <!-- 下部分 -->
					              <div class="card-bottom">
					                <el-button
					                  type="text"
					                  size="small"
					                  @click="handleDetail(item)"
					                >
					                  详情
					                </el-button>
					                <el-button
					                  type="text"
					                  size="small"
					                  @click="handleCardPriceEdit(item)"
					                  v-if="$store.getters.hasPermission('system:info:fix')"
					                >
					                  调价
					                </el-button>
					                <el-button
					                  type="text"
					                  size="small"
					                  @click="handleEdit(item)"
					                  v-if="$store.getters.hasPermission('system:info:edit')"
					                >
					                  修改
					                </el-button>
					                <el-button
					                  type="text"
					                  size="small"
					                  @click="handleDelete(item)"
					                >
					                  删除
					                </el-button>
					              </div>
					            </el-card>
					          </div>
					        </div>
					      </el-col>
					    </el-row>
					  </div>
					  <div v-else class="empty-card">
					    <el-empty description="暂无药品数据">
					      <el-button type="primary" icon="el-icon-plus" @click="openAddDialog">
					        添加药品
					      </el-button>
					    </el-empty>
					  </div>
					</div>
				</div>

				<!-- 底部分页 -->
				<div class="pagination-container">
					<div class="page-size">
						每页显示
						<el-select
							v-model="pageSize"
							size="mini"
							style="width: 80px; margin: 0 8px;"
							@change="handlePageSizeChange"
						>
							<el-option label="20" :value="20" />
							<el-option label="50" :value="50" />
							<el-option label="100" :value="100" />
						</el-select>
						<span style="margin-right: 20px;">共{{ total }}条</span>
					</div>
					<el-pagination
						:current-page="currentPage"
						:page-size="pageSize"
						:total="total"
						layout="prev, pager, next, jumper"
						background
						@current-change="handleCurrentChange"
					/>
				</div>
			</div>
		</div>

		<!-- 为药品指定售价模态框 -->
		<el-dialog
			:title="`为${currentDrugInfo ? currentDrugInfo.drugName : ''}指定售价`"
			:visible.sync="priceDialogVisible"
			width="600px"
			:close-on-click-modal="false"
			custom-class="custom-dialog"
		>
			<div class="price-setting-dialog">
				<!-- 上半部分：指定售价开关 -->
				<div class="price-dialog-top">
					<div class="price-switch-container">
						<div class="switch-header">
							<span class="switch-label">指定售价</span>
							<el-switch
								v-model="priceSwitchValue"
								:active-value="1"
								:inactive-value="0"
								active-color="#13ce66"
								inactive-color="#ff4949"
								:disabled="switchLoading || isSwitching"
								@change="handlePriceSwitchChange"
							>
							</el-switch>
						</div>
						
						<!-- 提示文字 -->
						<div v-if="priceSwitchValue === 0 && !isSwitching" class="price-switch-tip">
							<p class="tip-text">
								<i class="el-icon-warning"></i>
								当前药品{{ currentDrugInfo ? currentDrugInfo.drugName : '' }}还未开启允许指定售价功能，请先打开允许指定售价功能再进行操作！
							</p>
						</div>
						
						<!-- 加载状态提示 -->
						<div v-if="isSwitching" class="switch-loading-tip">
							<el-alert
								:title="`正在${priceSwitchValue === 1 ? '开启' : '关闭'}指定售价功能...`"
								type="info"
								:closable="false"
								show-icon
							></el-alert>
						</div>
					</div>
				</div>
				
				<!-- 下半部分：价格设置 - 左右布局 -->
				<div 
					class="price-dialog-bottom" 
					:class="{ 
						'price-content-disabled': priceSwitchValue === 0 || isSwitching 
					}"
				>
					<div class="price-settings">
						<!-- 使用el-row实现左右布局 -->
						<el-row :gutter="20" class="price-row">
							<!-- 左侧：最小指定售价 -->
							<el-col :span="12" class="price-col">
								<div class="price-setting-item">
									<label class="price-setting-label">最小指定售价：</label>
									<div class="price-input-wrapper">
										<el-input
											v-model="minSalePrice"
											size="mini"
											:disabled="priceSwitchValue === 0 || isSwitching"
											@input="handlePriceInput($event, 'min')"
											class="price-input"
										>
											<template slot="prepend">¥</template>
										</el-input>
										<div class="price-input-tip">大于0的正数，最多8位小数</div>
									</div>
								</div>
							</el-col>
							
							<!-- 右侧：包装指定售价 -->
							<el-col :span="12" class="price-col">
								<div class="price-setting-item">
									<label class="price-setting-label">包装指定售价：</label>
									<div class="price-input-wrapper">
										<el-input
											v-model="pacSalePrice"
											size="mini"
											:disabled="priceSwitchValue === 0 || isSwitching"
											@input="handlePriceInput($event, 'pac')"
											class="price-input"
										>
											<template slot="prepend">¥</template>
										</el-input>
										<div class="price-input-tip">大于0的正数，最多8位小数</div>
									</div>
								</div>
							</el-col>
						</el-row>
						
						<!-- 价格对比展示 -->
						<div v-if="originalPrices.minSalePrice && originalPrices.pacSalePrice" class="price-comparison">
							<div class="comparison-title">价格对比：</div>
							<div class="comparison-row">
								<el-row :gutter="20">
									<el-col :span="12">
										<div class="comparison-item">
											<span class="comparison-label">最小指定售价：</span>
											<div class="comparison-values">
												<span class="original-price">原价：¥{{ originalPrices.minSalePrice }}</span>
												<i class="el-icon-right comparison-arrow"></i>
												<span class="new-price">新价：¥{{ minSalePrice }}</span>
											</div>
										</div>
									</el-col>
									<el-col :span="12">
										<div class="comparison-item">
											<span class="comparison-label">包装指定售价：</span>
											<div class="comparison-values">
												<span class="original-price">原价：¥{{ originalPrices.pacSalePrice }}</span>
												<i class="el-icon-right comparison-arrow"></i>
												<span class="new-price">新价：¥{{ pacSalePrice }}</span>
											</div>
										</div>
									</el-col>
								</el-row>
							</div>
						</div>
					</div>
				</div>
			</div>
			
			<div slot="footer" class="dialog-footer">
				<el-button @click="cancelPriceEdit" size="mini">取消</el-button>
				<el-button 
					type="primary" 
					@click="confirmPriceEdit" 
					size="mini" 
					:loading="priceLoading"
					:disabled="priceSwitchValue === 0 || isSwitching"
				>
					确认调价
				</el-button>
			</div>
		</el-dialog>

		<!-- 表格列设置模态框 -->
		<el-dialog
			title="表格设置"
			:visible.sync="columnSettingVisible"
			top="4vh"
			:close-on-click-modal="false"
			custom-class="custom-dialog"
		>
			<el-tabs v-model="activeTab" type="card" class="settings-tabs">
				<!-- 动态显示字段标签页 - 引入FieldManagement组件 -->
				<el-tab-pane label="动态显示字段" name="display">
					<div class="dynamic-fields-settings">
						<field-management 
							ref="fieldManagement" 
							:current-fields="dynamicFields"
							@fields-change="handleDynamicFieldsChange" 
						/>
					</div>
				</el-tab-pane>
			</el-tabs>
			
			<div slot="footer" class="dialog-footer">
				<el-button @click="columnSettingVisible = false" size="mini">取消</el-button>
				<el-button type="primary" @click="saveDynamicFields" size="mini">确定</el-button>
			</div>
		</el-dialog>
		
		<!-- 快速录入西药品档案对话框 -->
		<el-dialog
			title="快速录入西药品档案"
			:visible.sync="excelDialogVisible"
			width="80%"
			top="10vh"
			:modal="false"
			custom-class="custom-dialog excel-import-dialog"
		>
			<template slot="title">
				<span style="color: white; font-size: 16px; font-weight: bold;">快速录入西药品档案</span>
			</template>
			
			<div class="excel-import-container">
				<!-- 左侧上传区域 -->
				<div class="left-panel">
					<!-- 上传头部 -->
					<div class="upload-header">
						<el-upload
							class="upload-demo file-uploader"
							drag
							action=""
							:auto-upload="false"
							:file-list="excelFileList"
							:before-upload="beforeExcelUpload"
							:on-change="handleExcelChange"
							:on-remove="handleFileRemove"
							accept=".xlsx, .xls"
							ref="fileUpload"
						>
							<i class="el-icon-upload"></i>
							<div class="el-upload__text">将Excel文件拖到此处，或<em>点击上传</em></div>
							<div class="el-upload__tip" slot="tip">只能上传xlsx/xls文件，且不超过10MB</div>
						</el-upload>
					</div>

					<!-- 文件信息区域 -->
					<div class="file-info">
						<div class="file-type-select">
							<el-form label-width="80px" size="mini">
								<el-form-item label="文件类型">
									<el-select v-model="fileType">
										<el-option label="Excel" value="excel"></el-option>
									</el-select>
								</el-form-item>
								<el-form-item label="文件路径">
									<el-input v-model="filePath" placeholder="上传后自动显示文件路径" readonly>
										<el-button slot="append" icon="el-icon-refresh" @click="reSelectFile"
											:disabled="!excelFileList.length">重新选择</el-button>
									</el-input>
								</el-form-item>
							</el-form>
						</div>

						<!-- 文件列表表格 -->
						<div class="file-table">
							<el-table :data="fileList" border height="188">
								<el-table-column style="font-size: 12px;" prop="name" label="文件名称" width="180"></el-table-column>
								<el-table-column style="font-size: 12px;" prop="path" label="文件路径" show-overflow-tooltip></el-table-column>
								<el-table-column style="font-size: 12px;" prop="status" label="状态" width="100">
									<template slot-scope="{row}">
										<el-tag :type="row.status === '导入成功' ? 'success' : 'danger'" size="mini">
											{{row.status}}
										</el-tag>
									</template>
								</el-table-column>
								<el-table-column label="操作" width="80" align="center">
									<template slot-scope="{row}">
										<el-button type="danger" icon="el-icon-delete" size="mini" circle
											@click="removeFile(row)"></el-button>
									</template>
								</el-table-column>
							</el-table>
						</div>
					</div>

					<!-- 底部操作按钮 -->
					<div class="action-footer">
						<el-button type="primary" size="medium" @click="submitExcel" :loading="loading"
							:disabled="!excelTableData.length">立即执行</el-button>
					</div>
				</div>

				<!-- 右侧预览区域 -->
				<div class="right-panel">
					<div class="preview-header">
						<h4>数据预览 (共 {{ excelTableData.length }} 条记录)</h4>
					</div>

					<!-- 预览表格 -->
					<div class="preview-table" v-if="excelTableData.length > 0">
						<el-table :data="excelTableData" border height="300" v-loading="loading">
							<el-table-column v-for="col in excelColumns" :key="col.prop" :prop="col.prop"
								:label="col.label" :width="col.width" show-overflow-tooltip></el-table-column>
						</el-table>
					</div>

					<!-- 空状态 -->
					<div v-else class="empty-preview">
						<i class="el-icon-document"></i>
						<div class="empty-text">暂无数据</div>
						<div class="empty-tip">请先上传Excel文件</div>
					</div>

					<!-- 错误信息表格 -->
					<div class="error-info-section" v-if="errorData.length > 0">
						<div class="error-header">
							<h4 style="color: #F56C6C;">
								<i class="el-icon-warning"></i>
								导入错误信息 (共 {{ errorData.length }} 条错误)
							</h4>
							<el-button type="danger" size="mini" icon="el-icon-download" @click="downloadErrorExcel"
								:loading="downloadingError">
								下载错误信息
							</el-button>
						</div>
						<!-- 错误信息表格 -->
						<div class="error-table">
						  <el-table :data="errorData" border height="200" style="width: 100%" v-loading="loading">
						    <!-- 错误原因列 - 新增在最左侧 -->
						    <el-table-column prop="msg" label="错误原因" width="150" show-overflow-tooltip fixed="left">
						      <template slot-scope="{row}">
						        <span style="color: #F56C6C;">{{ row.msg }}</span>
						      </template>
						    </el-table-column>
						    
						    <!-- 动态生成与预览表格一致的列 -->
						    <el-table-column 
						      v-for="col in excelColumns" 
						      :key="col.prop" 
						      :prop="col.prop" 
						      :label="col.label" 
						      :width="col.width" 
						      show-overflow-tooltip
						    />
						    
						    <!-- 操作列 -->
						    <el-table-column label="操作" width="80" align="center" fixed="right">
						      <template slot-scope="{row, $index}">
						        <el-button type="text" size="mini" @click="copyErrorInfo(row)">复制</el-button>
						      </template>
						    </el-table-column>
						  </el-table>
						</div>
						<div class="error-summary">
							<el-alert :title="`导入出错了~请检查：共发现 ${errorData.length} 条错误记录`" type="error" :closable="false"
								show-icon></el-alert>
						</div>
					</div>

					<!-- 分页区域 -->
					<div class="pagination-footer" v-if="excelTableData.length > 0">
						<el-pagination small layout="prev, pager, next" :total="excelTableData.length"
							:page-size="pageSize" @current-change="handlePageChange"></el-pagination>
					</div>
				</div>
			</div>
		</el-dialog>
		
		<!-- 模板下载选择对话框 -->
		<el-dialog
			title="导入模板下载"
			:visible.sync="templateDownloadVisible"
			width="500px"
			:close-on-click-modal="false"
			custom-class="custom-dialog"
		>
			<div class="template-download-content">
				<p class="download-tip">请选择要下载的模板类型：</p>
				<div class="template-options">
					<el-radio-group v-model="selectedTemplate">
						<el-radio label="exc_xymu">西药目录导入模板</el-radio>
						<el-radio label="exc_xykrk">西药采购入库导入模板</el-radio>
					</el-radio-group>
				</div>
				<div class="template-description">
					<p v-if="selectedTemplate === 'exc_xymu'">用于批量导入西药目录信息的Excel模板文件</p>
					<p v-if="selectedTemplate === 'exc_xykrk'">用于批量导入西药采购入库信息的Excel模板文件</p>
				</div>
			</div>
			
			<div slot="footer" class="dialog-footer">
				<el-button @click="templateDownloadVisible = false" size="mini">取消</el-button>
				<el-button 
					type="primary" 
					@click="downloadSelectedTemplate" 
					size="mini" 
					:loading="downloadingTemplate"
					:disabled="!selectedTemplate"
				>
					下载模板
				</el-button>
			</div>
		</el-dialog>
		<!-- 导出进度提示 -->
		<el-dialog title="导出进度" :visible.sync="exporting" width="30%" :close-on-click-modal="false" :show-close="false">
			<div style="text-align: center;">
				<el-progress type="circle" :percentage="exportProgress"></el-progress>
				<p style="margin-top: 20px;">正在生成导出文件，请稍候...</p>
			</div>
		</el-dialog>
		
		<!-- 新增/编辑/详情对话框子组件 -->
		<addModel 
			ref="addModel" 
			:parent-method="loadDrugList" 
			@refresh="handleRefresh"
			:mode="dialogMode"
			:currentEditData="currentEditData"
		></addModel>
	</div>
</template>

<script>
// 接口
import { getDrugList, getDosageForm, UpdateFixPric, deleteDrugP, exportAllWmDrug, importExcel, getDInfos, updateDrugInformation } from "@/api/systemCatalogue/MedicenCata.js";

// 子组件
import addModel from './Western/addDialogVisible.vue';
import FieldManagement from './field.vue';

// 新增导入
import * as XLSX from 'xlsx';

export default {
	name: 'Western',
	components: {
		addModel,
		FieldManagement
	},
	data() {
		return {
			// 搜索相关
			searchForm: '',
			searchKeyword: '',
			
			// 视图切换
			isCardView: false,
			
			// 分页相关
			currentPage: 1,
			pageSize: 20,
			total: 0,
			
			// 树形菜单相关
			treeData: [
				{
					id: 'all',
					dosageName: '全部剂型',
					count: 0,
					children: []
				}
			],
			treeProps: {
				children: 'children',
				label: 'dosageName'
			},
			treeLoading: false,
			treePageNum: 1,
			treePageSize: 50,
			treeTotal: 0,
			treeHasMore: true,
			treeScrollLoading: false,
			selectedTreeId: '',
			
			// 筛选相关
			timeFilterLabel: '全部时间',
			expensiveFilterLabel: '全部',
			timeFilter: 'all',
			expensiveFilter: 'all',
			
			// 表格列设置相关
			activeTab: 'display',
			columnSettingVisible: false,
			dynamicFields: [
				{ id: 1, name: "药品编码", prop: "code", show: true },
				{ id: 2, name: "药品名称", prop: "drugName", show: true },
				{ id: 3, name: "药品规格", prop: "drugSpec", show: true },
				{ id: 4, name: "药品剂型", prop: "dosForm", show: true },
				{ id: 5, name: "包装规格", prop: "pacSpec", show: true },
				{ id: 6, name: "包装数量", prop: "pacCnt", show: true },
				{ id: 7, name: "包装单位", prop: "pacUnt", show: true },
				{ id: 8, name: "指定售价", prop: "isFixPrice", show: true },
				{ id: 9, name: "最小指定售价", prop: "fixMinSalePric", show: true },
				{ id: 10, name: "包装指定售价", prop: "fixPacSalePric", show: true },
				{ id: 11, name: "手工划价", prop: "manualPric", show: true },
				{ id: 12, name: "给药方式", prop: "ruteName", show: true },
				{ id: 13, name: "单位剂量", prop: "unitDose", show: true },
				{ id: 14, name: "剂量单位", prop: "dosUnt", show: true },
				{ id: 15, name: "生产厂家", prop: "manufacturerName", show: true },
				{ id: 16, name: "基本药物", prop: "basMednB", show: true },
				{ id: 17, name: "抗生素否", prop: "antcB", show: true },
				{ id: 18, name: "贵重类型", prop: "expensiveType", show: true },
				{ id: 19, name: "操作人", prop: "createId", show: true },
				{ id: 20, name: "操作时间", prop: "createTime", show: true }
			],
			
			// 表格加载状态
			tableLoading: false,
			
			// 药品列表数据
			drugList: [],
			
			// 调价模态框相关
			priceDialogVisible: false,
			currentDrugInfo: null,
			isSwitching: false, // 新增：标记是否正在切换开关状态
			priceSwitchValue: 0,
			switchLoading: false,
			minSalePrice: '0',
			pacSalePrice: '0',
			originalPrices: {
				minSalePrice: '0',
				pacSalePrice: '0'
			},
			priceLoading: false,
			switchTimer: null,
			
			// 对话框模式
			dialogMode: 'add',
			currentEditData: null,
			
			// Excel导入相关
			excelDialogVisible: false,
			excelFileList: [], // 上传文件列表
			excelTableData: [], // 表格数据
			excelColumns: [], // 表格列
			fileType: 'excel',
			filePath: '',
			fileList: [], // 文件信息列表
			errorData: [], // 错误数据
			downloadingError: false,
			loading: false,
			
			// 导入模板下载相关
			templateDownloadVisible: false,
			selectedTemplate: 'exc_xymu',
			downloadingTemplate: false,
			templatePaths: {
				exc_xymu: 'https://fsapi.nfcis.cn/outpatient/excel/西药目录导入模板.xlsx',
				exc_xykrk: 'https://fsapi.nfcis.cn/outpatient/excel/西药库(中药库)入库导入模板.xlsx'
			},
			
			// 导出相关
			exporting: false,
			exportProgress: 0
		}
	},
	computed: {
		// 筛选后的药品列表
		filteredDrugList() {
			let list = [...this.drugList]
			
			// 时间筛选
			if (this.timeFilter !== 'all') {
				const now = new Date()
				list = list.filter(item => {
					if (!item.createTime) return false
					const createTime = new Date(item.createTime)
					switch (this.timeFilter) {
						case 'today':
							return createTime.toDateString() === now.toDateString()
						case 'week':
							const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000)
							return createTime >= weekAgo
						case 'month':
							const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000)
							return createTime >= monthAgo
						case 'year':
							const yearAgo = new Date(now - 365 * 24 * 60 * 60 * 1000)
							return createTime >= yearAgo
						default:
							return true
					}
				})
			}
			
			// 贵重类型筛选
			if (this.expensiveFilter !== 'all') {
				list = list.filter(item => {
					return String(item.expensiveType) === this.expensiveFilter
				})
			}
			
			return list
		}
	},
	mounted() {
		this.loadTreeData()
		this.loadDrugList()
		this.initializeColumnSettings()
	},
	methods: {
		// 加载树形菜单数据
		async loadTreeData() {
			try {
				this.treeLoading = true
				const params = {
					pageNum: this.treePageNum,
					pageSize: this.treePageSize
				}
				const res = await getDosageForm(params)
				
				if (res && res.data) {
					const list = res.data || []
					if (this.treePageNum === 1) {
						this.treeData[0].children = list
						this.treeData[0].count = list.length
					} else {
						this.treeData[0].children = [...this.treeData[0].children, ...list]
						this.treeData[0].count = this.treeData[0].children.length
					}
					this.treeTotal = list.length || 0
					this.treeHasMore = list.length === this.treePageSize
				}
			} catch (error) {
				console.error('加载树形菜单失败:', error)
				this.$message.error('加载剂型数据失败')
			} finally {
				this.treeLoading = false
				this.treeScrollLoading = false
			}
		},
		
		// 加载药品列表数据
		async loadDrugList() {
			try {
				this.tableLoading = true
				const params = {
					drugName: this.searchKeyword,
					dosform: this.selectedTreeId || '',
					pageNum: this.currentPage,
					pageSize: this.pageSize
				}
				const res = await getDrugList(params)
				
				if (res && res.data) {
					this.drugList = res.data.content || []
					this.total = res.data.totalSize || 0
				}
			} catch (error) {
				console.error('加载药品列表失败:', error)
				this.$message.error('加载药品数据失败')
				this.drugList = []
				this.total = 0
			} finally {
				this.tableLoading = false
			}
		},
		
		// 树节点点击
		handleNodeClick(data) {
			this.selectedTreeId = data.id === 'all' ? '' : data.id
			this.currentPage = 1
			this.loadDrugList()
		},
		
		// 搜索剂型
		handleSearchForm() {
			console.log('搜索剂型:', this.searchForm)
		},
		
		// 搜索药品
		handleSearch() {
			this.currentPage = 1
			this.loadDrugList()
		},
		
		// 分页变化
		handleCurrentChange(val) {
			this.currentPage = val
			this.loadDrugList()
		},
		
		// 每页显示条数变化
		handlePageSizeChange(val) {
			this.pageSize = val
			this.currentPage = 1
			this.loadDrugList()
		},
		
		// 视图切换
		handleViewSwitch(val) {
			console.log('视图切换:', val ? '卡片视图' : '表格视图')
		},
		
		// 详情查看
		handleDetail(row) {
		  this.currentEditData = {
			...row
		  };
		  this.dialogMode = 'view';  // 设置为查看模式
		  
		  this.$nextTick(() => {
			if (this.$refs.addModel && this.$refs.addModel.openAddModel) {
			  // 构建查看模式的标题
			  const drugName = row.drugName || '未知药品';
			  const title = `查看西药(${drugName})的档案信息`;
			  
			  this.$refs.addModel.openAddModel({
				id: row.id,
				mode: 'view',  // 明确设置为查看模式
				label: title,  // 使用构建的标题
				drugName: drugName
			  });
			} else {
			  console.error('addModel组件或openAddModel方法未找到');
			  this.$message.error('组件加载失败，请刷新页面重试');
			}
		  });
		},
		
		// 修改
		handleEdit(row) {
		  this.currentEditData = {
			...row
		  };
		  this.dialogMode = 'edit';
		  
		  this.$nextTick(() => {
			if (this.$refs.addModel && this.$refs.addModel.openAddModel) {
			  // 构建查看模式的标题
			  const drugName = row.drugName || '未知药品';
			  const title = `修改西药(${drugName})的档案信息`;
			  
			  this.$refs.addModel.openAddModel({
				id: row.id,
				mode: 'edit',  // 明确设置为查看模式
				label: title,  // 使用构建的标题
				drugName: drugName
			  });
			} else {
			  console.error('addModel组件或openAddModel方法未找到');
			  this.$message.error('组件加载失败，请刷新页面重试');
			}
		  });
		},
		
		// 删除药品
		handleDelete(row) {
			this.$confirm('确定删除该药品吗?', '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(async () => {
				try {
					const res = await deleteDrugP({
						drugId: row.id
					});
		
					if (res.code === 200) {
						this.$message.success('删除成功');
						this.loadDrugList();
					} else {
						this.$message.error(res.message || '删除失败');
					}
				} catch (error) {
					console.error('删除药品失败:', error);
					this.$message.error(res.message || '删除失败');
				}
			}).catch(() => {
				this.$message.info('已取消删除');
			});
		},
		
		// 添加药品
		openAddDialog() {
			this.currentEditData = null;
			this.dialogMode = 'add';
			
			this.$nextTick(() => {
				if (this.$refs.addModel && this.$refs.addModel.openAddModel) {
					this.$refs.addModel.openAddModel({
						mode: 'add',
						label: '添加西药'
					});
				} else {
					console.error('addModel组件或openAddModel方法未找到');
					this.$message.error('组件加载失败，请刷新页面重试');
				}
			});
		},
		
		// 刷新方法
		handleRefresh() {
			this.loadDrugList()
		},
		
		// 时间筛选
		handleTimeFilter(command) {
			this.timeFilter = command
			const labels = {
				all: '全部时间',
				today: '今天',
				week: '一周前',
				month: '一月前',
				year: '一年前'
			}
			this.timeFilterLabel = labels[command] || '全部时间'
		},
		
		// 贵重类型筛选
		handleExpensiveFilter(command) {
			this.expensiveFilter = command
			const labels = {
				all: '全部',
				'1': '普价',
				'2': '贵重',
				'3': '昂贵',
				'4': '免费'
			}
			this.expensiveFilterLabel = labels[command] || '全部'
		},
		
		// 树形菜单滚动加载
		handleTreeScroll() {
			const treeContent = this.$refs.treeContent
			if (treeContent) {
				const scrollTop = treeContent.scrollTop
				const scrollHeight = treeContent.scrollHeight
				const clientHeight = treeContent.clientHeight
				
				// 滚动到底部且还有更多数据
				if (scrollTop + clientHeight >= scrollHeight - 10 && 
					this.treeHasMore && 
					!this.treeScrollLoading && 
					!this.treeLoading) {
					this.treeScrollLoading = true
					this.treePageNum += 1
					this.loadTreeData()
				}
			}
		},
		
		// 获取卡片主题类
		getCardThemeClass(expensiveType) {
			const themeClass = {
				'1': 'card-theme-primary',
				'2': 'card-theme-warning',
				'3': 'card-theme-danger',
				'4': 'card-theme-success'
			}
			return themeClass[expensiveType] || 'card-theme-default'
		},
		
		// 表格行类名方法 - 根据贵重类型设置背景色
		tableRowClassName({ row }) {
			if (row.expensiveType) {
				switch (String(row.expensiveType)) {
					case '1': // 普价
						return 'expensive-type-primary'
					case '2': // 贵重
						return 'expensive-type-warning'
					case '3': // 昂贵
						return 'expensive-type-danger'
					case '4': // 免费
						return 'expensive-type-success'
					default:
						return 'expensive-type-info'
				}
			}
			return ''
		},
		
		// 获取贵重类型标签类型
		getExpensiveTypeTagType(expensiveType) {
			const typeMap = {
				'1': '', // 普价 - 默认
				'2': 'warning', // 贵重
				'3': 'danger', // 昂贵
				'4': 'success' // 免费
			}
			return typeMap[expensiveType] || ''
		},
		
		// 获取贵重类型标签文本
		getExpensiveTypeLabel(expensiveType) {
			const labelMap = {
				'1': '普价',
				'2': '贵重',
				'3': '昂贵',
				'4': '免费'
			}
			return labelMap[expensiveType] || '-'
		},
		
		// 获取药典名称的tooltip内容
		getTooltipContent(row) {
			const stateText = row.state === 1 ? '正常' : row.state === 0 ? '异常' : '未知'
			const expensiveTypeText = this.getExpensiveTypeLabel(row.expensiveType)
			
			return `${row.drugName}\n状态: ${stateText}\n贵重类型: ${expensiveTypeText}`
		},
		
		// 表格列设置
		handleColumnSetting() {
			this.columnSettingVisible = true
		},
		
		// 初始化列设置
		initializeColumnSettings() {
			// 从本地存储加载列设置
			const savedSettings = localStorage.getItem('westernDynamicFields')
			if (savedSettings) {
				try {
					const parsed = JSON.parse(savedSettings)
					this.dynamicFields = parsed || this.dynamicFields
				} catch (error) {
					console.error('加载列设置失败:', error)
				}
			}
		},
		
		// 保存动态字段设置
		saveDynamicFields() {
			this.columnSettingVisible = false
		},
		
		// 处理动态字段变化
		handleDynamicFieldsChange(fields) {
			this.dynamicFields = fields
		},
		
		// 列表模式下点击调价
		async handlePriceEdit(row) {
			try {
				this.priceLoading = true
				
				// 调用接口获取药品详细信息，参数修改为 { id: row.id }
				const res = await getDInfos({ id: row.id })
				
				if (res.code === 200) {
					// 保存药品信息
					this.currentDrugInfo = res.data
					
					// 设置价格开关值
					this.priceSwitchValue = res.data.isFixPrice || 0
					
					// 设置价格
					// 如果已开启指定售价但价格为0，设置为1
					if (res.data.isFixPrice === 1) {
						this.minSalePrice = res.data.fixMinSalePric && parseFloat(res.data.fixMinSalePric) > 0 
							? res.data.fixMinSalePric 
							: '1'
						this.pacSalePrice = res.data.fixPacSalePric && parseFloat(res.data.fixPacSalePric) > 0
							? res.data.fixPacSalePric
							: '1'
					} else {
						this.minSalePrice = res.data.fixMinSalePric || '0'
						this.pacSalePrice = res.data.fixPacSalePric || '0'
					}
					
					// 保存原始价格用于对比
					this.originalPrices = {
						minSalePrice: this.minSalePrice,
						pacSalePrice: this.pacSalePrice
					}
					
					// 打开模态框
					this.priceDialogVisible = true
				} else {
					this.$message.error(res.message || '获取药品信息失败')
				}
			} catch (error) {
				console.error('获取药品信息失败:', error)
				this.$message.error('获取药品信息失败，请稍后重试')
			} finally {
				this.priceLoading = false
			}
		},
		
		// 卡片模式下点击调价
		handleCardPriceEdit(row) {
			this.handlePriceEdit(row)
		},
		
		// 处理价格输入
		handlePriceInput(value, type) {
			// 验证输入格式
			const validatedValue = this.validatePriceInput(value)
			
			if (type === 'min') {
				this.minSalePrice = validatedValue
			} else if (type === 'pac') {
				this.pacSalePrice = validatedValue
			}
		},
		
		// 验证价格输入
		validatePriceInput(value) {
			// 移除非数字、小数点、负号的字符
			let validatedValue = value.replace(/[^\d.]/g, '')
			
			// 确保只有一个小数点
			const parts = validatedValue.split('.')
			if (parts.length > 2) {
				validatedValue = parts[0] + '.' + parts.slice(1).join('')
			}
			
			// 限制小数位最多8位
			if (parts.length === 2 && parts[1].length > 8) {
				validatedValue = parts[0] + '.' + parts[1].substring(0, 8)
			}
			
			// 如果以小数点开头，在前面加0
			if (validatedValue.startsWith('.')) {
				validatedValue = '0' + validatedValue
			}
			
			// 确保不是负数
			if (validatedValue.startsWith('-')) {
				validatedValue = validatedValue.substring(1)
			}
			
			// 移除开头多余的0
			if (validatedValue.length > 1 && validatedValue.startsWith('0') && !validatedValue.startsWith('0.')) {
				validatedValue = validatedValue.replace(/^0+/, '')
			}
			
			return validatedValue
		},
		
		// 处理价格开关变化
		async handlePriceSwitchChange(val) {
			// 如果已经在切换状态，直接返回，防止重复点击
			if (this.isSwitching) {
				return
			}
			
			// 设置切换状态
			this.isSwitching = true
			
			try {
				// 准备更新数据
				const updateData = {
					...this.currentDrugInfo,
					isFixPrice: val
				}
				
				// 如果开关从关闭变为打开（val === 1），设置默认价格为1
				if (val === 1) {
					// 设置默认价格为1
					updateData.fixMinSalePric = '1'
					updateData.fixPacSalePric = '1'
					
					// 更新对话框中的价格显示
					this.minSalePrice = '1'
					this.pacSalePrice = '1'
					
					// 更新原始价格记录
					this.originalPrices.minSalePrice = '1'
					this.originalPrices.pacSalePrice = '1'
					
					console.log('开关打开，设置默认价格为1')
				} else if (val === 0) {
					// 开关关闭，不修改价格，保持原有值
					updateData.fixMinSalePric = this.currentDrugInfo.fixMinSalePric || '0'
					updateData.fixPacSalePric = this.currentDrugInfo.fixPacSalePric || '0'
				}
				
				// 调用更新接口
				const res = await updateDrugInformation(updateData)
				
				if (res.code === 200) {
					this.$message.success(val === 1 ? '已开启指定售价功能' : '已关闭指定售价功能')
					
					// 更新本地数据
					this.currentDrugInfo.isFixPrice = val
					this.priceSwitchValue = val
					
					// 如果开关关闭，清空价格
					if (val === 0) {
						this.minSalePrice = '0'
						this.pacSalePrice = '0'
					}
					
					// 重新加载列表数据
					this.loadDrugList()
					
					// 只有在成功后才启用下半部分内容
					// 注意：这里的启用/禁用是通过priceSwitchValue控制的
					// 由于我们已经更新了priceSwitchValue，下半部分会自动启用/禁用
				} else {
					// 如果更新失败，恢复开关状态
					this.priceSwitchValue = val === 1 ? 0 : 1
					this.$message.error(res.message || '更新失败')
				}
			} catch (error) {
				console.error('更新药品信息失败:', error)
				// 如果更新失败，恢复开关状态
				this.priceSwitchValue = val === 1 ? 0 : 1
				this.$message.error('更新失败，请稍后重试')
			} finally {
				// 清除切换状态
				this.isSwitching = false
			}
		},
				
			// 确认调价
			async confirmPriceEdit() {
				// 验证价格
				const minPriceNum = parseFloat(this.minSalePrice)
				const pacPriceNum = parseFloat(this.pacSalePrice)
				
				if (isNaN(minPriceNum) || isNaN(pacPriceNum)) {
					this.$message.error('价格必须为数字')
					return
				}
				
				if (minPriceNum <= 0 || pacPriceNum <= 0) {
					this.$message.error('价格必须大于0')
					return
				}
				
				this.priceLoading = true
				
				try {
					// 调用调价接口
					const response = await UpdateFixPric({
						drugId: this.currentDrugInfo.id,
						fixMinSalePric: this.minSalePrice,
						fixPacSalePric: this.pacSalePrice
					})
					
					if (response.code === 200) {
						this.$message.success('调价成功')
						this.priceDialogVisible = false
						
						// 重新加载数据
						this.loadDrugList()
					} else {
						this.$message.error(response.message || '调价失败')
					}
				} catch (error) {
					console.error('调价接口调用失败:', error)
					this.$message.error('调价失败，请稍后重试')
				} finally {
					this.priceLoading = false
				}
			},
			
			// 取消调价
			cancelPriceEdit() {
				this.priceDialogVisible = false
				this.resetPriceDialogData()
			},
			
			// 重置调价对话框数据
			resetPriceDialogData() {
				this.currentDrugInfo = null
				this.priceSwitchValue = 0
				this.switchLoading = false
				this.minSalePrice = '0'
				this.pacSalePrice = '0'
				this.originalPrices = {
					minSalePrice: '0',
					pacSalePrice: '0'
				}
				
				// 清除定时器
				if (this.switchTimer) {
					clearTimeout(this.switchTimer)
					this.switchTimer = null
				}
			},
			
			// 获取药品名称的第一个字符
			getFirstChar(name) {
				if (!name) return '药'
				return name.charAt(0)
			},
			
			// 检查文本是否溢出
			isTextOverflow(text, maxLength) {
				if (!text) return false
				return text.length > maxLength
			},
			
			// 格式化时间
			formatTime(time) {
				if (!time) return '-'
				return time
			},
			
			// 导入按钮点击
			handleImport() {
				this.excelDialogVisible = true
				this.resetImportData()
			},
			
			// 重置导入数据
			resetImportData() {
			  this.excelFileList = [];
			  this.excelTableData = [];
			  this.excelColumns = [];
			  this.fileList = [];
			  this.errorData = [];
			  this.filePath = '';
			  this.loading = false;
			  this.downloadingError = false;
			},
			
			// Excel上传前的验证
			beforeExcelUpload(file) {
				const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
								file.type === 'application/vnd.ms-excel'
				const isLt10M = file.size / 1024 / 1024 < 10
				
				if (!isExcel) {
					this.$message.error('只能上传Excel文件!')
				}
				if (!isLt10M) {
					this.$message.error('文件大小不能超过10MB!')
				}
				
				return isExcel && isLt10M
			},
			
			// 处理Excel文件变化
			async handleExcelChange(file, fileList) {
			  try {
			    this.loading = true
			    
			    // 更新文件列表
			    this.excelFileList = fileList
			    
			    // 读取Excel文件
			    const data = await this.readExcelFile(file.raw)
			    
			    // 处理读取的数据
			    if (data && data.length > 0) {
			      // 确保数据格式正确，转换为接口需要的格式
			      this.excelTableData = data.map(item => {
			        const processedItem = {};
			        
			        // 处理每个字段，确保字段名与接口一致
			        Object.keys(item).forEach(key => {
			          const newKey = this.normalizeFieldName(key);
			          const value = item[key];
			          
			          // 处理特殊字段
			          if (newKey === 'code') {
			            processedItem[newKey] = String(value).trim();
			          } else if (newKey === 'drugName') {
			            processedItem[newKey] = String(value).trim();
			          } else if (newKey === 'drugSpec') {
			            processedItem[newKey] = String(value).trim();
			          } else if (newKey === 'manufacturerName') {
			            processedItem[newKey] = String(value).trim();
			          } else if (newKey === 'dosForm') {
			            processedItem[newKey] = String(value).trim();
			          } else if (newKey === 'pacSpec') {
			            processedItem[newKey] = String(value).trim();
			          } else if (newKey === 'unitDose') {
			            processedItem[newKey] = String(value).trim();
			          } else if (newKey === 'dosUnt') {
			            processedItem[newKey] = String(value).trim();
			          } else if (newKey === 'pacCnt') {
			            // 确保包装数量是数字
			            processedItem[newKey] = isNaN(Number(value)) ? 0 : Number(value);
			          } else if (newKey === 'isFixPrice') {
			            // 处理布尔值字段
			            processedItem[newKey] = value === 1 || value === '1' || value === '是' || value === true ? 1 : 0;
			          } else if (newKey === 'manualPric') {
			            // 处理布尔值字段
			            processedItem[newKey] = value === 1 || value === '1' || value === '是' || value === true ? 1 : 0;
			          } else if (typeof value === 'string') {
			            processedItem[newKey] = value.trim();
			          } else {
			            processedItem[newKey] = value;
			          }
			        });
			        
			        return processedItem;
			      });
			      
			      // 提取表头
			      if (this.excelTableData.length > 0) {
			        const headers = Object.keys(this.excelTableData[0]);
			        this.excelColumns = headers.map(header => ({
			          prop: header,
			          label: this.getFieldDisplayName(header),
			          width: 150
			        }));
			      }
			      
			      // 更新文件信息
			      this.filePath = file.name;
			      this.fileList = [{
			        name: file.name,
			        path: file.name,
			        status: '待导入'
			      }];
			      
			      this.$message.success(`成功读取${this.excelTableData.length}条数据`)
			    } else {
			      this.$message.error('Excel文件中没有数据')
			    }
			  } catch (error) {
			    console.error('读取Excel文件失败:', error)
			    this.$message.error('读取Excel文件失败: ' + error.message)
			  } finally {
			    this.loading = false
			  }
			},
			
			// 规范化字段名称
			normalizeFieldName(fieldName) {
			  const fieldMap = {
			    '药品编码': 'code',
			    '药品名称': 'drugName',
			    '药品规格': 'drugSpec',
			    '生产厂家': 'manufacturerName',
			    '药品剂型': 'dosForm',
			    '包装规格': 'pacSpec',
			    '单位剂量': 'unitDose',
			    '剂量单位': 'dosUnt',
			    '包装数量': 'pacCnt',
			    '是否指定售价': 'isFixPrice',
			    '是否允许手工划价': 'manualPric',
			    '备注': 'remark',
			    '包装单位': 'pacUnt',
			    '拼音简码': 'pyCode',
			    '药品别名': 'alis',
			    '贵重类型': 'expensiveType',
			    '给药方式': 'ruteName',
			    '基本药物': 'basMednB',
			    '抗生素否': 'antcB',
			    '医保名称': 'chsName',
			    '医保编码': 'chsCode',
			    'YPID': 'ypId',
			    '操作人': 'createId',
			    '操作时间': 'createTime'
			  };
			  
			  return fieldMap[fieldName] || fieldName;
			},
			
			// 获取字段显示名称
			getFieldDisplayName(fieldName) {
			  const displayMap = {
			    'code': '药品编码',
			    'drugName': '药品名称',
			    'drugSpec': '药品规格',
			    'manufacturerName': '生产厂家',
			    'dosForm': '药品剂型',
			    'pacSpec': '包装规格',
			    'unitDose': '单位剂量',
			    'dosUnt': '剂量单位',
			    'pacCnt': '包装数量',
			    'isFixPrice': '是否指定售价',
			    'manualPric': '是否允许手工划价',
			    'remark': '备注',
			    'pacUnt': '包装单位',
			    'pyCode': '拼音简码',
			    'alis': '药品别名',
			    'expensiveType': '贵重类型',
			    'ruteName': '给药方式',
			    'basMednB': '基本药物',
			    'antcB': '抗生素否',
			    'chsName': '医保名称',
			    'chsCode': '医保编码',
			    'ypId': 'YPID',
			    'createId': '操作人',
			    'createTime': '操作时间'
			  };
			  
			  return displayMap[fieldName] || fieldName;
			},
			
				// 移除文件
				handleFileRemove(file, fileList) {
					this.excelFileList = fileList
					if (fileList.length === 0) {
						this.resetImportData()
					}
				},
				
				// 重新选择文件
				reSelectFile() {
				  // 重置所有相关数据
				  this.resetImportData();
				  
				  // 清空上传文件列表
				  this.$refs.fileUpload && (this.$refs.fileUpload.uploadFiles = []);
				  
				  // 触发文件选择
				  this.$refs.fileUpload.$refs['upload-inner'].handleClick();
				},
				
				// 重置所有数据
				resetAllData() {
				  // 清空错误信息表格
				  this.errorData = [];
				  // 清空数据预览表格
				  this.excelTableData = [];
				  this.excelColumns = [];
				  // 清空文件列表
				  this.excelFileList = [];
				  this.fileList = [];
				  this.filePath = '';
				  // 重置加载状态
				  this.loading = false;
				  this.downloadingError = false;
				},
				
				// 从文件列表移除
				removeFile(row) {
					const index = this.fileList.findIndex(file => file.name === row.name)
					if (index !== -1) {
						this.fileList.splice(index, 1)
						this.excelFileList = []
						this.excelTableData = []
						this.excelColumns = []
						this.errorData = []
						this.filePath = ''
					}
				},
				
				// 读取Excel文件
				readExcelFile(file) {
					return new Promise((resolve, reject) => {
						const reader = new FileReader()
						
						reader.onload = (e) => {
							try {
								const data = new Uint8Array(e.target.result)
								const workbook = XLSX.read(data, { type: 'array' })
								const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
								const jsonData = XLSX.utils.sheet_to_json(firstSheet)
								
								resolve(jsonData)
							} catch (error) {
								reject(new Error('读取Excel文件失败: ' + error.message))
							}
						}
						
						reader.onerror = () => {
							reject(new Error('读取文件失败'))
						}
						
						reader.readAsArrayBuffer(file)
					})
				},
				
				// 提交Excel导入
				async submitExcel() {
					if (!this.excelFileList.length) {
						this.$message.warning('请先上传Excel文件');
						return;
					}
				
					this.loading = true;
					try {
						const formData = new FormData();
						formData.append('file', this.excelFileList[0].raw);
						formData.append('type', 'western');
				
						const response = await importExcel(formData);
				
						if (response.code === 200) {
							if (response.data && Object.keys(response.data).length > 0) {
								this.handleImportErrors(response.data);
								this.$message.warning(`导入完成，发现 ${Object.keys(response.data).length} 条错误记录`);
							} else {
								this.handleImportSuccess();
							}
						} else {
							this.$message.error(response.message || '导入失败');
						}
					} catch (error) {
						console.error('导入失败:', error);
						this.$message.error('导入失败');
					} finally {
						this.loading = false;
					}
				},
				
				// 处理导入错误
				handleImportErrors(errorData) {
				  console.log('导入错误数据:', errorData);
				  this.errorData = [];
				
				  if (typeof errorData === 'object' && errorData !== null) {
				    // 处理不同格式的错误数据
				    if (Array.isArray(errorData)) {
				      errorData.forEach((error, index) => {
				        this.processSingleError(error, index);
				      });
				    } else {
				      Object.entries(errorData).forEach(([rowKey, errorInfo]) => {
				        this.processSingleError(errorInfo, rowKey);
				      });
				    }
				  }
				
				  this.$message.warning(`导入完成，发现 ${this.errorData.length} 条错误记录`);
				},
				
				// 处理单个错误项
				processSingleError(errorInfo, index) {
				  const errorItem = {
				    id: this.errorData.length + 1,
				    rowNumber: this.extractRowNumber(errorInfo, index),
				    msg: errorInfo.msg || errorInfo.message || errorInfo.error || '未知错误',
				  };
				
				  // 从原始数据中提取所有字段
				  this.extractAllTemplateFields(errorItem, errorInfo);
				
				  this.errorData.push(errorItem);
				},
				
				// 提取模板所有字段
				extractAllTemplateFields(errorItem, apiData) {
				  // 定义完整的字段映射
				  const allFields = {
				    'code': this.findFieldValue(apiData, ['code', '药品编码']),
				    'drugName': this.findFieldValue(apiData, ['drugName', '药品名称']),
				    'drugSpec': this.findFieldValue(apiData, ['drugSpec', '药品规格']),
				    'manufacturerName': this.findFieldValue(apiData, ['manufacturerName', '生产厂家']),
				    'dosForm': this.findFieldValue(apiData, ['dosForm', '药品剂型']),
				    'pacSpec': this.findFieldValue(apiData, ['pacSpec', '包装规格']),
				    'unitDose': this.findFieldValue(apiData, ['unitDose', '单位剂量', 'unitDose']),
				    'dosUnt': this.findFieldValue(apiData, ['dosUnt', '剂量单位']),
				    'pacCnt': this.findFieldValue(apiData, ['pacCnt', '包装数量']),
				    'isFixPrice': this.findFieldValue(apiData, ['isFixPrice', '是否指定售价']),
				    'manualPric': this.findFieldValue(apiData, ['manualPric', '是否允许手工划价']),
				    'remark': this.findFieldValue(apiData, ['remark', '备注']),
				    'pacUnt': this.findFieldValue(apiData, ['pacUnt', '包装单位']),
				    'pyCode': this.findFieldValue(apiData, ['pyCode', '拼音简码']),
				    'alis': this.findFieldValue(apiData, ['alis', '药品别名']),
				    'expensiveType': this.findFieldValue(apiData, ['expensiveType', '贵重类型']),
				    'ruteName': this.findFieldValue(apiData, ['ruteName', '给药方式']),
				    'basMednB': this.findFieldValue(apiData, ['basMednB', '基本药物']),
				    'antcB': this.findFieldValue(apiData, ['antcB', '抗生素否']),
				    'chsName': this.findFieldValue(apiData, ['chsName', '医保名称']),
				    'chsCode': this.findFieldValue(apiData, ['chsCode', '医保编码']),
				    'ypId': this.findFieldValue(apiData, ['ypId', 'YPID']),
				    'createId': this.findFieldValue(apiData, ['createId', '操作人']),
				    'createTime': this.findFieldValue(apiData, ['createTime', '操作时间']),
				    'markUpName': this.findFieldValue(apiData, ['markUpName', '加成方式']),
				    'markUpRate': this.findFieldValue(apiData, ['markUpRate', '加成比例']),
				    'markUpPric': this.findFieldValue(apiData, ['markUpPric', '加成金额']),
				    'fixMinSalePric': this.findFieldValue(apiData, ['fixMinSalePric', '指定小单位售价']),
				    'fixPacSalePric': this.findFieldValue(apiData, ['fixPacSalePric', '指定包装单位售价']),
				    'minDose': this.findFieldValue(apiData, ['minDose', '最小剂量']),
				    'minUnt': this.findFieldValue(apiData, ['minUnt', '最小单位']),
				    // 特别注意：接口返回的字段名是approvalNumber和code
				    'code': this.findFieldValue(apiData, ['code', 'code', '商品条码']),
				    'approvalNo': this.findFieldValue(apiData, ['approvalNo', 'approvalNumber', '批准文号'])
				  };
				
				  // 合并所有字段
				  Object.assign(errorItem, allFields);
				  
				  // 保存原始数据
				  errorItem.originalData = { ...apiData };
				},
				
				// 从API数据中提取所有字段
				extractAllFieldsFromApiData(errorItem, apiData) {
				  // 接口返回的字段映射
				  const fieldMapping = {
				    // 药品基本信息
				    'drugName': this.findFieldValue(apiData, ['drugName', '药品名称']),
				    'code': this.findFieldValue(apiData, ['code', 'drugCode', '药品编码']),
				    'drugSpec': this.findFieldValue(apiData, ['drugSpec', '药品规格']),
				    'dosForm': this.findFieldValue(apiData, ['dosForm', '药品剂型']),
				    'pacSpec': this.findFieldValue(apiData, ['pacSpec', '包装规格']),
				    'pacCnt': this.findFieldValue(apiData, ['pacCnt', '包装数量']),
				    'pacUnt': this.findFieldValue(apiData, ['pacUnt', '包装单位']),
				    
				    // 价格相关字段
				    'isFixPrice': this.findFieldValue(apiData, ['isFixPrice', '是否指定售价']),
				    'manualPric': this.findFieldValue(apiData, ['manualPric', '是否允许手工划价']),
				    'fixMinSalePric': this.findFieldValue(apiData, ['fixMinSalePric', '指定小单位售价']),
				    'fixPacSalePric': this.findFieldValue(apiData, ['fixPacSalePric', '指定包装单位售价']),
				    
				    // 加成相关字段
				    'markUpName': this.findFieldValue(apiData, ['markUpName', '加成方式']),
				    'markUpRate': this.findFieldValue(apiData, ['markUpRate', '加成比例']),
				    'markUpPric': this.findFieldValue(apiData, ['markUpPric', '加成金额']),
				    
				    // 生产厂家
				    'manufacturerName': this.findFieldValue(apiData, ['manufacturerName', '生产厂家']),
				    
				    // 剂量相关
				    'unitDose': this.findFieldValue(apiData, ['unitDose', '单位剂量']),
				    'dosUnt': this.findFieldValue(apiData, ['dosUnt', '剂量单位']),
				    'minDose': this.findFieldValue(apiData, ['minDose', '最小剂量']),
				    'minUnt': this.findFieldValue(apiData, ['minUnt', '最小单位']),
				    
				    // 其他字段
				    'pyCode': this.findFieldValue(apiData, ['pyCode', '拼音简码']),
				    'alis': this.findFieldValue(apiData, ['alis', '药品别名']),
				    'expensiveType': this.findFieldValue(apiData, ['expensiveType', '贵重类型']),
				    'ruteName': this.findFieldValue(apiData, ['ruteName', '给药方式']),
				    'basMednB': this.findFieldValue(apiData, ['basMednB', '基本药物']),
				    'antcB': this.findFieldValue(apiData, ['antcB', '是否抗生素']),
				    'remark': this.findFieldValue(apiData, ['remark', '备注']),
				    
				    // 新增字段
				    'approvalNo': this.findFieldValue(apiData, ['approvalNo', '批准文号']),
				    'code': this.findFieldValue(apiData, ['code', '商品条码']),
				  };
				
				  // 合并所有字段到errorItem
				  Object.assign(errorItem, fieldMapping);
				
				  // 从原始数据中提取任何其他字段
				  for (const key in apiData) {
				    if (apiData[key] !== undefined && apiData[key] !== null && !errorItem[key]) {
				      errorItem[key] = apiData[key];
				    }
				  }
				},
				
				// 提取行号
				extractRowNumber(errorInfo, index) {
				  if (errorInfo.rowNumber !== undefined) return errorInfo.rowNumber;
				  if (errorInfo.lineNumber !== undefined) return errorInfo.lineNumber;
				  if (!isNaN(parseInt(index))) return parseInt(index) + 2; // 从第2行开始（表头在第1行）
				  return index;
				},
				
				// 从多个可能的字段名中查找值
				findFieldValue(data, fieldNames) {
				  for (const fieldName of fieldNames) {
				    if (data[fieldName] !== undefined && data[fieldName] !== null && data[fieldName] !== '') {
				      return data[fieldName];
				    }
				  }
				  return '';
				},
				
				// 修改：提取所有字段
				extractAllFields(errorItem, errorInfo) {
					// 提取第一张截图中的字段
					const firstImageFields = {
						'drugName': this.findFieldValue(errorInfo, ['drugName', '药品名称', 'name']),
						'pacSpec': this.findFieldValue(errorInfo, ['pacSpec', '包装规格', 'pacSpec']),
						'manufacturerName': this.findFieldValue(errorInfo, ['manufacturerName', '生产厂家', 'manufacturer',
							'producer'
						]),
						'markUpName': this.findFieldValue(errorInfo, ['markUpName', '加成方式', 'markUpName']),
						'markUpRate': this.findFieldValue(errorInfo, ['markUpRate', '加成比例', 'markUpRate']),
						'markUpPric': this.findFieldValue(errorInfo, ['markUpPric', '加成金额', 'markUpPric']),
						'manualPric': this.findFieldValue(errorInfo, ['manualPric', '是否允许手工划价', 'manualPrice']),
						'isFixPrice': this.findFieldValue(errorInfo, ['isFixPrice', '是否指定售价', 'fixedPrice']),
						'fixMinSalePric': this.findFieldValue(errorInfo, ['fixMinSalePric', '指定小单位售价', 'fixMinSalePric']),
						'fixPacSalePric': this.findFieldValue(errorInfo, ['fixPacSalePric', '指定包装单位售价', 'fixPacSalePric']),
						'drugSpec': this.findFieldValue(errorInfo, ['drugSpec', '药品规格', 'drugSpec']),
						'unitDose': this.findFieldValue(errorInfo, ['unitDose', '单位计量', 'unitDose']),
						
						'dosUnt': this.findFieldValue(errorInfo, ['dosUnt', '剂量单位', 'doseUnit']),
						'pacUnt': this.findFieldValue(errorInfo, ['pacUnt', '包装单位', 'packageUnit']),
						'minUnt': this.findFieldValue(errorInfo, ['minUnt', '最小单位', 'minimumUnit'])
					};
				
					// 提取第二张截图中的额外字段
					const secondImageFields = {
						
						'unitDose': this.findFieldValue(errorInfo, ['unitDose', '单位计量', 'unitDose']),
						'minDose': this.findFieldValue(errorInfo, ['minDose', '最小剂量', 'minimumDose']),
						'pacCnt': this.findFieldValue(errorInfo, ['pacCnt', '包装数量', 'packageCount']),
						'drugSpec': this.findFieldValue(errorInfo, ['drugSpec', '药品规格', 'drugSpecification']),
						'code': this.findFieldValue(errorInfo, ['code', '商品条码', 'code']),
						'approvalNo': this.findFieldValue(errorInfo, ['approvalNo', '批准文号', 'approvalNumber'])
					};
				
					// 合并所有字段
					Object.assign(errorItem, firstImageFields, secondImageFields);
				
					// 如果原始数据存在，保留所有字段
					if (errorInfo.originalData) {
						Object.assign(errorItem, errorInfo.originalData);
					}
				},
				
				// 新增：提取原始数据
				extractOriginalData(errorInfo) {
					// 排除错误信息字段，保留原始数据字段
					const originalData = {
						...errorInfo
					};
					delete originalData.msg;
					delete originalData.message;
					delete originalData.error;
					delete originalData.rowNumber;
					delete originalData.lineNumber;
				
					return Object.keys(originalData).length > 0 ? originalData : null;
				},
				
				// 新增：提取药品信息，确保字段与预览表格一致
				extractDrugInfo(errorItem, errorInfo) {
					// 从原始数据中提取与预览表格一致的字段
					const fieldMapping = {
						// 药品基本信息
						'drugCode': '药品编码',
						'drugName': '药品名称',
						'drugSpec': '药品规格',
						'manufacturerName': '生产厂家',
						'dosageForm': '药品剂型',
						
						// 其他字段映射
						'markUpName': '加成方式',
						'markUpPric': '加成金额',
						'markUpRate': '加成比例',
						'pacSpec': '包装规格',
						'unitDose': '单位剂量',
						'dosUnt': '剂量单位',
						'pacCnt': '包装数量',
						'isFixPrice': '是否指定售价',
						'manualPric': '是否允许手工划价',
						'ruteName': '给药方式',
						'basMednB': '基本药物',
						'antcB': '是否抗生素',
						'expensiveType': '贵重类型',
						'clinicId': '隶属诊所',
						'chsName': '医保名称',
						'chsCode': '医保编码',
						'ypId': 'YPID',
						'createId': '操作人',
						'createTime': '操作时间',
						'remark': '备注'
					};
				
					// 将原始数据映射到标准字段
					Object.keys(fieldMapping).forEach(field => {
						if (errorInfo[field] !== undefined) {
							errorItem[field] = errorInfo[field];
						}
					});
				
					// 如果原始数据存在，也保存一份
					if (errorInfo.originalData) {
						Object.assign(errorItem, errorInfo.originalData);
					}
				},
				
				// 处理导入成功
				handleImportSuccess() {
					this.$message.success('Excel导入成功！');
					this.excelDialogVisible = false;
					this.errorData = [];
					this.loadDrugList();
					this.resetImportData();
				},
				
				// 格式化错误信息
				formatErrorInfo(errorInfo) {
					if (typeof errorInfo === 'string') return errorInfo;
					if (typeof errorInfo === 'object') {
						return errorInfo.msg || errorInfo.message || JSON.stringify(errorInfo);
					}
					return '未知错误';
				},
				
				// 下载错误信息Excel
				downloadErrorExcel() {
				  try {
				    this.downloadingError = true;
				    
				    if (this.errorData.length === 0) {
				      this.$message.warning('没有错误信息可下载');
				      return;
				    }
				    
				    // 创建工作簿
				    const wb = XLSX.utils.book_new();
				    
				    // 准备工作表数据
				    const wsData = [];
				    
				    // 定义表头 - 按照导入模板字段顺序
				    const templateHeaders = [
				      '药品名称',
				      '包装规格',
				      '生产厂家',
				      '加成方式',
				      '加成金额',
				      '加成比例',
				      '是否允许手工划价',
				      '是否指定售价',
				      '指定小单位售价',
				      '指定包装单位售价',
				      '剂量单位',
				      '包装单位',
				      '最小单位',
				      '单位计量',
				      '最小剂量',
				      '包装数量',
				      '药品规格',
				      '商品条码',
				      '批准文号'
				    ];
				    
				    // 创建表头：错误原因 + 模板标准字段
				    const headers = ['错误原因', ...templateHeaders];
				    wsData.push(headers);
				    
				    // 字段映射关系
				    const fieldMapping = {
				      '药品名称': 'drugName',
				      '包装规格': 'pacSpec',
				      '生产厂家': 'manufacturerName',
				      '加成方式': 'markUpName',
				      '加成金额': 'markUpPric',
				      '加成比例': 'markUpRate',
				      '是否允许手工划价': 'manualPric',
				      '是否指定售价': 'isFixPrice',
				      '指定小单位售价': 'fixMinSalePric',
				      '指定包装单位售价': 'fixPacSalePric',
				      '剂量单位': 'dosUnt',
				      '包装单位': 'pacUnt',
				      '最小单位': 'minUnt',
				      '单位计量': 'unitDose',
				      '最小剂量': 'minDose',
				      '包装数量': 'pacCnt',
				      '药品规格': 'drugSpec',
				      '商品条码': 'code',
				      '批准文号': 'approvalNo'
				    };
				    
				    // 添加数据行
				    this.errorData.forEach(item => {
				      const row = [item.msg || '']; // 第一列是错误原因
				      
				      // 按照模板字段顺序添加数据
				      templateHeaders.forEach(header => {
				        const fieldName = fieldMapping[header];
				        let value = '';
				        
				        if (fieldName) {
				          // 从错误数据中查找对应字段
				          value = item[fieldName] || '';
				          
				          // 如果为空，尝试从原始数据中查找
				          if (value === '' && item.originalData) {
				            // 特殊处理商品条码和批准文号
				            if (fieldName === 'code') {
				              value = item.originalData.code || item.originalData.code || '';
				            } else if (fieldName === 'approvalNo') {
				              value = item.originalData.approvalNo || item.originalData.approvalNumber || '';
				            } else {
				              value = item.originalData[fieldName] || '';
				            }
				          }
				          
				          // 处理特殊字段的格式
				          if (header === '是否指定售价' && (value === 1 || value === '1')) {
				            value = '是';
				          } else if (header === '是否指定售价' && (value === 0 || value === '0')) {
				            value = '否';
				          }
				          
				          if (header === '是否允许手工划价' && (value === 1 || value === '1')) {
				            value = '是';
				          } else if (header === '是否允许手工划价' && (value === 0 || value === '0')) {
				            value = '否';
				          }
				          
				          // 处理数值字段
				          if (fieldName === 'markUpRate' || fieldName === 'markUpPric' || 
				              fieldName === 'fixMinSalePric' || fieldName === 'fixPacSalePric') {
				            if (value === null || value === undefined) {
				              value = '';
				            }
				          }
				        }
				        
				        row.push(value);
				      });
				      
				      wsData.push(row);
				    });
				    
				    // 创建工作表
				    const ws = XLSX.utils.aoa_to_sheet(wsData);
				    
				    // 设置列宽
				    const colWidths = [{ wch: 30 }]; // 错误原因列宽
				    
				    // 设置其他列的宽度
				    templateHeaders.forEach(() => {
				      colWidths.push({ wch: 15 });
				    });
				    
				    ws['!cols'] = colWidths;
				    
				    // 添加到工作簿
				    XLSX.utils.book_append_sheet(wb, ws, '错误信息');
				    
				    // 生成Excel文件
				    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
				    
				    // 创建下载链接
				    const blob = new Blob([wbout], { 
				      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
				    });
				    const url = window.URL.createObjectURL(blob);
				    const link = document.createElement('a');
				    link.href = url;
				    link.download = `西药导入错误信息_${new Date().getTime()}.xlsx`;
				    document.body.appendChild(link);
				    link.click();
				    document.body.removeChild(link);
				    window.URL.revokeObjectURL(url);
				    
				    this.$message.success('错误信息导出成功');
				  } catch (error) {
				    console.error('导出错误信息失败:', error);
				    this.$message.error('导出错误信息失败: ' + error.message);
				  } finally {
				    this.downloadingError = false;
				  }
				},
				
				// 复制错误信息
				copyErrorInfo(row) {
				  const fields = [
				    { label: '药品名称', key: 'drugName' },
				    { label: '包装规格', key: 'pacSpec' },
				    { label: '生产厂家', key: 'manufacturerName' },
				    { label: '加成方式', key: 'markUpName' },
				    { label: '加成金额', key: 'markUpPric' },
				    { label: '加成比例', key: 'markUpRate' },
				    { label: '是否允许手工划价', key: 'manualPric' },
				    { label: '是否指定售价', key: 'isFixPrice' },
				    { label: '指定小单位售价', key: 'fixMinSalePric' },
				    { label: '指定包装单位售价', key: 'fixPacSalePric' },
				    { label: '剂量单位', key: 'dosUnt' },
				    { label: '包装单位', key: 'pacUnt' },
				    { label: '最小单位', key: 'minUnt' },
				    { label: '单位计量', key: 'unitDose' },
				    { label: '最小剂量', key: 'minDose' },
				    { label: '包装数量', key: 'pacCnt' },
				    { label: '药品规格', key: 'drugSpec' },
				    { label: '商品条码', key: 'code' },
				    { label: '批准文号', key: 'approvalNo' }
				  ];
				  
				  let text = `错误原因: ${row.msg || ''}\n\n`;
				  text += '字段信息:\n';
				  
				  fields.forEach(field => {
				    const value = row[field.key] || (row.originalData && row.originalData[field.key]) || '';
				    text += `${field.label}: ${value}\n`;
				  });
				  
				  const textArea = document.createElement('textarea');
				  textArea.value = text;
				  document.body.appendChild(textArea);
				  textArea.select();
				  
				  try {
				    document.execCommand('copy');
				    this.$message.success('错误信息已复制到剪贴板');
				  } catch (err) {
				    this.$message.error('复制失败');
				  }
				  
				  document.body.removeChild(textArea);
				},
				
				// 导入模板下载
				async handleCloudDownload() {
					this.templateDownloadVisible = true
					this.selectedTemplate = 'exc_xymu'
				},
				
				// 下载选中的模板
				async downloadSelectedTemplate() {
					this.downloadingTemplate = true
					try {
						const templatePath = this.templatePaths[this.selectedTemplate]
						if (!templatePath) {
							this.$message.error('模板路径未配置，请联系管理员')
							return
						}
		
						// 显示下载提示
						this.$message.info('开始下载模板文件...')
		
						// 方法1：直接使用window.open
						const downloadWindow = window.open(templatePath, '_blank')
		
						// 检查是否被浏览器拦截
						setTimeout(() => {
							if (!downloadWindow || downloadWindow.closed || downloadWindow.location.href === 'about:blank') {
								this.$message.warning('浏览器可能阻止了弹出窗口，请允许弹出窗口或使用方法2')
								this.tryAlternativeDownloadMethod(this.selectedTemplate, templatePath)
							}
						}, 1000)
		
						this.$message.success('模板下载开始，请检查浏览器下载列表')
						this.templateDownloadVisible = false
					} catch (error) {
						console.error('下载模板失败:', error)
						this.$message.error('下载失败: ' + error.message)
		
						// 尝试备用下载方法
						this.tryAlternativeDownloadMethod(this.selectedTemplate, this.templatePaths[this.selectedTemplate])
					} finally {
						this.downloadingTemplate = false
					}
				},
				
				// 备用下载方法
				tryAlternativeDownloadMethod(templateType, templatePath) {
					try {
						// 方法2：使用a标签下载
						const link = document.createElement('a')
						link.href = templatePath
						link.download = this.getTemplateFileName(templateType)
						link.style.display = 'none'
						link.target = '_blank'
		
						// 添加时间戳避免缓存问题
						link.href = this.addTimestampToUrl(templatePath)
		
						document.body.appendChild(link)
						link.click()
		
						// 清理
						setTimeout(() => {
							document.body.removeChild(link)
						}, 100)
		
						this.$message.info('已尝试使用a标签下载，请检查浏览器下载列表')
		
					} catch (error) {
						console.error('备用下载方法失败:', error)
						this.showManualDownloadGuide(templatePath, templateType)
					}
				},
				
				// 添加时间戳避免缓存
				addTimestampToUrl(url) {
					if (url.indexOf('?') === -1) {
						return url + '?t=' + Date.now()
					} else {
						return url + '&t=' + Date.now()
					}
				},
				
				// 显示手动下载指导
				showManualDownloadGuide(templatePath, templateType) {
					this.$confirm(
						`模板下载失败！\n\n可能原因：\n• 浏览器安全策略阻止\n• 网络连接问题\n• 文件服务器配置问题\n\n请尝试以下方法：\n1. 直接点击链接访问：${templatePath}\n2. 右键链接另存为\n3. 联系管理员检查文件服务器配置`,
						'下载失败', {
						confirmButtonText: '复制下载链接',
						cancelButtonText: '关闭',
						type: 'error',
						customClass: 'download-guide-dialog',
						showClose: true
					}
					).then(() => {
						this.copyToClipboard(templatePath)
					}).catch(() => {
						// 用户点击关闭
					})
				},
				
				// 复制到剪贴板
				copyToClipboard(text) {
					const textArea = document.createElement('textarea')
					textArea.value = text
					textArea.style.position = 'fixed'
					textArea.style.top = 0
					textArea.style.left = 0
					textArea.style.opacity = 0
					document.body.appendChild(textArea)
					textArea.focus()
					textArea.select()
		
					try {
						const successful = document.execCommand('copy')
						if (successful) {
							this.$message.success('链接已复制到剪贴板')
						} else {
							this.$message.error('复制失败，请手动复制')
						}
					} catch (err) {
						this.$message.error('复制失败，请手动复制')
					}
		
					document.body.removeChild(textArea)
				},
				
				// 获取模板文件名
				getTemplateFileName(templateType) {
					const names = {
						'exc_xymu': '西药目录导入模板.xlsx',
						'exc_xykrk': '西药采购入库导入模板.xlsx'
					}
					return names[templateType] || 'template.xlsx'
				},
				
				// 导出
				async handleExport() {
					this.$confirm('确定要导出药品数据吗？', '导出确认', {
						confirmButtonText: '确定导出',
						cancelButtonText: '取消',
						type: 'warning'
					}).then(async () => {
						await this.handleExportData()
					}).catch(() => {
						this.$message.info('已取消导出')
					})
				},
				
				// 执行导出
				async handleExportData() {
					this.exporting = true;
					this.exportProgress = 0;
				
					try {
						this.loading = true;
				
						// 构建查询参数
						const params = {
							drugName: this.searchValue,
							condis: this.treeValue
						};
				
						// 调用导出接口
						const response = await exportAllWmDrug();
				
						if (response) {
							// 处理二进制数据
							await this.processExportData(response);
						} else {
							this.$message.error('导出数据为空');
						}
					} catch (error) {
						console.error('导出失败:', error);
						this.$message.error('导出失败: ' + (error.message || '未知错误'));
					} finally {
						this.loading = false;
					}
				},
				// 处理导出数据
				async processExportData(blobData) {
					try {
						// 创建Blob对象
						const blob = new Blob([blobData], {
							type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
						});
				
						// 创建下载链接
						const url = window.URL.createObjectURL(blob);
						const link = document.createElement('a');
				
						// 生成文件名：当前诊所名称 + 西药目录列表 + 时间日期
						const clinicName = this.clinicName || '';
						const timestamp = new Date();
						const dateStr = timestamp.toLocaleDateString('zh-CN', {
							year: 'numeric',
							month: '2-digit',
							day: '2-digit'
						}).replace(/\//g, '');
				
						const timeStr = timestamp.toLocaleTimeString('zh-CN', {
							hour: '2-digit',
							minute: '2-digit',
							second: '2-digit'
						}).replace(/:/g, '');
				
						const fileName = `${clinicName}西药目录列表${dateStr}${timeStr}.xlsx`;
						const progressTimer = setInterval(() => {
							if (this.exportProgress < 90) {
								this.exportProgress += 10;
							}
						}, 300);
				
						// 设置下载属性
						link.href = url;
						link.download = fileName;
						link.style.display = 'none';
				
						// 触发下载
						document.body.appendChild(link);
						link.click();
				
						// 清理资源
						setTimeout(() => {
							document.body.removeChild(link);
							window.URL.revokeObjectURL(url);
							clearInterval(progressTimer);
							this.exportProgress = 100;
						}, 100);
				
						this.$message.success('导出成功，文件正在下载中...');
				
						this.exporting = false;
					} catch (error) {
						console.error('处理导出数据失败:', error);
						this.$message.error('处理导出文件失败: ' + error.message);
				
						// 尝试处理可能的错误响应（如JSON格式的错误信息）
						try {
							const text = await new Response(blobData).text();
							if (text.startsWith('{') || text.startsWith('[')) {
								const errorInfo = JSON.parse(text);
								this.$message.error(errorInfo.message || errorInfo.msg || '导出接口返回错误');
							}
						} catch (parseError) {
							// 忽略解析错误，使用原始错误信息
						}
					}
				},
				
			}
		}
		</script>
		
		<style lang="less" scoped>
		.western-container {
			height: calc(100vh - 140px);
			background: #f0f2f5;
			box-sizing: border-box;
			overflow: hidden;
		
			.main-layout {
				display: flex;
				height: 100%;
				background: #fff;
				border-radius: 4px;
				overflow: hidden;
				box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
			}
		
			.left-sidebar {
				width: 188px;
				border-right: 1px solid #e8e8e8;
				display: flex;
				flex-direction: column;
				overflow: hidden;
		
				.tree-header {
					padding: 12px 16px;
					border-bottom: 1px solid #e8e8e8;
					background: #fafafa;
				}
		
				.tree-content {
					flex: 1;
					overflow-y: auto;
					padding: 8px 0;
		
					.tree-node {
						display: flex;
						align-items: center;
						justify-content: space-between;
						width: 100%;
						font-size: 14px; /* 树形菜单文字大小调整为14px */
						padding: 0 8px;
						
						.tree-count {
							color: #999;
							font-size: 12px;
						}
					}
				}
			}
		
			.right-content {
				flex: 1;
				display: flex;
				flex-direction: column;
				overflow: hidden;
		
				.operation-bar {
					padding: 12px 16px;
					border-bottom: 1px solid #e8e8e8;
					display: flex;
					align-items: center;
					background: #fafafa;
		
					.search-container {
						flex: 1;
						.search-input {
							width: 288px;
						}
					}
		
					.button-group {
						display: flex;
						align-items: center;
						gap: 8px;
						flex-wrap: wrap;
						margin-left: 12px;
						.el-button {
							margin: 0;
						}
					}
				}
		
				.content-header {
					padding: 12px 16px;
					border-bottom: 1px solid #e8e8e8;
					display: flex;
					justify-content: space-between;
					align-items: center;
					
					.content-title {
						display: flex;
						align-items: center;
						
						.title-text {
							font-size: 16px;
							font-weight: bold;
							color: #333;
							margin-right: 20px;
						}
						
						.filter-group {
							display: flex;
							align-items: center;
							gap: 12px;
							
							.filter-dropdown {
								.filter-item {
									padding: 4px 12px;
									cursor: pointer;
									font-size: 12px;
									color: #606266;
									transition: all 0.2s;
									border: none !important; /* 去掉边框线 */
									
									&:hover {
										color: #409eff;
									}
								}
							}
						}
					}
					
					.view-switch {
						display: flex;
						align-items: center;
						
						.switch-label {
							font-size: 12px;
							color: #666;
							margin: 0 8px;
						}
					}
				}
		
				.content-area {
					flex: 1;
					overflow: hidden;
					padding: 0;
					position: relative;
					
					.table-container {
						height: 100%;
						overflow: hidden;
						position: relative;
						
						.expanded-content {
							padding: 16px;
							background: #fafafa;
							border-radius: 4px;
							margin: 4px 0;
							
							h4 {
								margin: 0 0 12px 0;
								color: #333;
								font-size: 14px;
							}
						}
						
						.empty-state {
							position: absolute;
							top: 50%;
							left: 50%;
							transform: translate(-50%, -50%);
							text-align: center;
							
							.el-empty {
								padding: 40px 0;
							}
							
							.el-button {
								margin-top: 12px;
							}
						}
					}
					
					// 添加新的卡片容器样式
					.card-container {
					  .card-list {
					    .drug-card-container {
					      position: relative;
					      margin-bottom: 20px;
					      height: 100%;
					      
					      .card-badge {
					        position: absolute;
					        top: 8px;
					        right: 8px;
					        z-index: 20;
					        
					        ::v-deep .el-badge__content {
					          font-size: 10px;
					          padding: 0 4px;
					          height: 16px;
					          line-height: 16px;
					          border-radius: 8px;
					          background-color: #f56c6c;
					          color: #fff;
					          border: 1px solid #fff;
					          box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
					          z-index: 20;
					        }
					      }
					      
					      .drug-card-wrapper {
					        height: 100%;
					        border-radius: 8px;
					        
					        .drug-card {
					          height: 100%;
					          border: none;
					          box-shadow: none;
					          background: transparent;
					          border-radius: 8px;
					          
					          .el-card__body {
					            padding: 12px;
					            height: 100%;
					            display: flex;
					            flex-direction: column;
					          }
					          
					          .card-top {
					            display: flex;
					            margin-bottom: 12px;
					            
					            .card-image {
					              width: 60px;
					              height: 60px;
					              margin-right: 12px;
					              flex-shrink: 0;
					              
					              .drug-image-default {
					                width: 100%;
					                height: 100%;
					                border-radius: 4px;
					                display: flex;
					                align-items: center;
					                justify-content: center;
					                background: rgba(255, 255, 255, 0.8);
					                color: #606266;
					                font-size: 20px;
					                font-weight: bold;
					                border: 1px solid rgba(220, 223, 230, 0.5);
					              }
					            }
					            
					            .card-info {
					              flex: 1;
					              min-width: 0;
					              
					              .drug-name {
					                font-size: 14px;
					                font-weight: bold;
					                color: #333;
					                margin-bottom: 4px;
					                overflow: hidden;
					                text-overflow: ellipsis;
					                white-space: nowrap;
					              }
					              
					              .drug-code {
					                font-size: 12px;
					                color: #606266;
					                margin-bottom: 4px;
					                overflow: hidden;
					                text-overflow: ellipsis;
					                white-space: nowrap;
					              }
					              
					              .drug-spec {
					                font-size: 12px;
					                color: #909399;
					                overflow: hidden;
					                text-overflow: ellipsis;
					                white-space: nowrap;
					              }
					            }
					          }
					          
					          .card-middle {
					            flex: 1;
					            margin-bottom: 12px;
					            
					            .price-info {
					              margin-bottom: 8px;
					              
					              .price-item {
					                display: flex;
					                align-items: center;
					                margin-bottom: 8px;
					                
					                .price-label {
					                  font-size: 12px;
					                  color: #909399;
					                  width: 90px;
					                  flex-shrink: 0;
					                }
					                
					                .price-value {
					                  font-size: 12px;
					                  color: #f56c6c;
					                  font-weight: bold;
					                  overflow: hidden;
					                  text-overflow: ellipsis;
					                  white-space: nowrap;
					                }
					              }
					            }
					            
					            .create-time {
					              font-size: 12px;
					              color: #909399;
					              display: flex;
					              
					              .time-label {
					                margin-right: 4px;
					                flex-shrink: 0;
					              }
					              
					              .time-value {
					                overflow: hidden;
					                text-overflow: ellipsis;
					                white-space: nowrap;
					              }
					            }
					          }
					          
					          .card-bottom {
					            display: flex;
					            justify-content: space-around;
					            border-top: 1px solid rgba(220, 223, 230, 0.5);
					            padding-top: 8px;
					            
					            .el-button {
					              padding: 0 4px;
					              font-size: 12px;
					            }
					          }
					        }
					        
					        // 各种主题样式
					        &.card-theme-primary {
					          background: linear-gradient(135deg, #ecf5ff 0%, #d9ecff 100%);
					          border: 1px solid #b3d8ff;
					        }
					        
					        &.card-theme-warning {
					          background: linear-gradient(135deg, #fdf6ec 0%, #faecd8 100%);
					          border: 1px solid #f5dab1;
					        }
					        
					        &.card-theme-danger {
					          background: linear-gradient(135deg, #fef0f0 0%, #fde2e2 100%);
					          border: 1px solid #fab6b6;
					        }
					        
					        &.card-theme-success {
					          background: linear-gradient(135deg, #e6ffe7 0%, #eeffef 100%);
					          border: 1px solid #d3d4d6;
					        }
					        
					        &.card-theme-default {
					          background: #fff;
					          border: 1px solid #e4e7ed;
					        }
					      }
					    }
					  }
					}
				}
		
				.pagination-container {
					padding: 12px 16px;
					border-top: 1px solid #e8e8e8;
					display: flex;
					justify-content: space-between;
					align-items: center;
					background: #fafafa;
					flex-shrink: 0;
		
					.page-size {
						display: flex;
						align-items: center;
						color: #606266;
						font-size: 13px;
					}
				}
			}
		}
		
		// 全局模态框样式
		::v-deep .custom-dialog {
			border-radius: 8px;
			overflow: hidden;
			
			.el-dialog__header {
				background: linear-gradient(135deg, #ebfaff 0%, #00557f 100%);
				padding: 12px;
				border-radius: 8px 8px 0 0;
				
				.el-dialog__title {
					color: white;
					font-size: 14px;
					font-weight: bold;
				}
				
				.el-dialog__close {
					color: white;
				}
			}
			
			.el-dialog__body {
				padding: 8px;
			}
			
			.dialog-footer {
				padding: 10px;
				text-align: right;
				border-top: 1px solid #e8e8e8;
			}
		}
		
		// 价格设置对话框样式
		.price-setting-dialog {
			.price-dialog-top {
				height: 20%;
				padding-bottom: 20px;
				border-bottom: 1px solid #e8e8e8;
				
				.price-switch-container {
					.switch-header {
						display: flex;
						justify-content: space-between;
						align-items: center;
						margin-bottom: 10px;
						
						.switch-label {
							font-size: 16px;
							font-weight: bold;
							color: #333;
						}
					}
					
					.price-switch-tip {
						background-color: #fef0f0;
						border: 1px solid #fde2e2;
						border-radius: 4px;
						padding: 10px;
						margin-top: 10px;
						
						.tip-text {
							margin: 0;
							font-style: italic;
							color: #f56c6c;
							font-size: 13px;
							display: flex;
							align-items: center;
							
							i {
								margin-right: 5px;
							}
						}
					}
				}
			}
			
			.price-dialog-bottom {
				height: 80%;
				padding-top: 20px;
				
				&.price-content-disabled {
					opacity: 0.6;
					pointer-events: none;
					
					.price-input-wrapper {
						::v-deep .el-input-group__prepend {
							background-color: #f5f7fa;
							border-color: #e4e7ed;
						}
					}
				}
				
				.price-settings {
					.price-setting-item {
						margin-bottom: 20px;
						
						.price-setting-label {
							display: block;
							margin-bottom: 8px;
							font-size: 14px;
							color: #333;
						}
						
						.price-input-wrapper {
							.price-input {
								::v-deep .el-input-group__prepend {
									padding: 0 10px;
								}
							}
							
							.price-input-tip {
								margin-top: 5px;
								font-size: 12px;
								color: #999;
							}
						}
					}
					
					.price-comparison {
						margin-top: 20px;
						padding: 15px;
						background-color: #f8f9fa;
						border-radius: 4px;
						
						.comparison-title {
							font-weight: bold;
							margin-bottom: 10px;
							color: #333;
						}
						
						.comparison-item {
							display: flex;
							align-items: center;
							margin-bottom: 8px;
							
							.comparison-label {
								width: 120px;
								font-size: 12px;
								color: #666;
							}
							
							.original-price {
								flex: 1;
								font-size: 12px;
								color: #f56c6c;
								text-decoration: line-through;
							}
							
							i {
								margin: 0 10px;
								color: #409eff;
							}
							
							.new-price {
								font-size: 12px;
								color: #67c23a;
								font-weight: bold;
							}
						}
					}
				}
			}
		}
		
		// Excel导入对话框样式
		.excel-import-container {
			display: flex;
			height: 666px;
			
			.left-panel {
				width: 40%;
				border-right: 1px solid #e8e8e8;
				display: flex;
				flex-direction: column;
				
				.upload-header {
					padding: 20px;
					border-bottom: 1px solid #e8e8e8;
					
					.upload-demo {
						.el-upload {
							width: 100%;
						}
						
						.el-upload-dragger {
							width: 100%;
							height: 180px;
							border: 2px dashed #409eff;
							background: #fafafa;
							border-radius: 8px;
							
							&:hover {
								border-color: #66b1ff;
							}
							
							.el-icon-upload {
								margin: 20px 0 16px;
								color: #409eff;
								font-size: 60px;
							}
							
							.el-upload__text {
								font-size: 14px;
								color: #606266;
								
								em {
									color: #409eff;
									font-style: normal;
								}
							}
						}
						
						.el-upload__tip {
							margin-top: 10px;
							color: #666;
							font-size: 12px;
						}
					}
				}
				
				.file-info {
					flex: 1;
					padding: 20px;
					overflow-y: auto;
					
					.file-table {
						margin-top: 20px;
						
						::v-deep .el-table {
							font-size: 12px;
						}
					}
				}
				
				.action-footer {
					padding: 20px;
					border-top: 1px solid #e8e8e8;
					text-align: center;
				}
			}
			
			.right-panel {
				width: 60%;
				padding: 20px;
				display: flex;
				flex-direction: column;
				overflow-y: auto;
				
				.preview-header {
					margin-bottom: 20px;
					
					h4 {
						margin: 0;
						font-size: 16px;
						color: #333;
					}
				}
				
				.preview-table {
					flex: 1;
					margin-bottom: 20px;
					
					::v-deep .el-table {
						font-size: 12px;
					}
				}
				
				.empty-preview {
					flex: 1;
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					color: #999;
					
					.el-icon-document {
						font-size: 60px;
						margin-bottom: 20px;
					}
					
					.empty-text {
						font-size: 16px;
						margin-bottom: 10px;
					}
					
					.empty-tip {
						font-size: 14px;
					}
				}
				
				.error-info-section {
					margin-top: 20px;
					
					.error-header {
						display: flex;
						justify-content: space-between;
						align-items: center;
						margin-bottom: 10px;
						
						h4 {
							margin: 0;
							
							.el-icon-warning {
								margin-right: 5px;
							}
						}
					}
					
					.error-table {
						margin-bottom: 10px;
						
						::v-deep .el-table {
							font-size: 12px;
						}
					}
				}
				
				.pagination-footer {
					margin-top: 20px;
					text-align: center;
				}
			}
		}
		
		// 动态字段设置样式
		.dynamic-fields-settings {
			padding: 10px;
		}
		
		// 模板下载对话框样式
		.template-download-content {
			.download-tip {
				margin: 0 0 20px 0;
				color: #333;
				font-size: 14px;
			}
			
			.template-options {
				margin-bottom: 20px;
				
				.el-radio-group {
					width: 100%;
					
					.el-radio {
						display: block;
						margin: 10px 0;
						padding: 10px;
						border: 1px solid #e4e7ed;
						border-radius: 4px;
						transition: all 0.3s;
						
						&:hover {
							border-color: #409eff;
							background-color: #f5f7fa;
						}
						
						&.is-checked {
							border-color: #409eff;
							background-color: #ecf5ff;
						}
					}
				}
			}
			
			.template-description {
				padding: 10px;
				background-color: #f5f7fa;
				border-radius: 4px;
				
				p {
					margin: 0;
					color: #666;
					font-size: 12px;
				}
			}
		}
		
		// 下载指导对话框样式
		::v-deep .download-guide-dialog {
			.el-message-box__content {
				white-space: pre-wrap;
				line-height: 1.6;
			}
		}
		
		// 根据贵重类型设置表格行背景色
		::v-deep .expensive-type-primary {
			background-color: #ecf5ff !important;
			
			&:hover {
				td {
					background-color: #d9ecff !important;
				}
			}
		}
		
		::v-deep .expensive-type-warning {
			background-color: #fdf6ec !important;
			
			&:hover {
				td {
					background-color: #faecd8 !important;
				}
			}
		}
		
		::v-deep .expensive-type-danger {
			background-color: #fef0f0 !important;
			
			&:hover {
				td {
					background-color: #fde2e2 !important;
				}
			}
		}
		
		::v-deep .expensive-type-success {
			background-color: #eeffef !important;
			
			&:hover {
				td {
					background-color: #eeffef !important;
				}
			}
		}
		
		::v-deep .expensive-type-info {
			background-color: #f4f4f5 !important;
			
			&:hover {
				td {
					background-color: #e9e9eb !important;
				}
			}
		}
		
		// 覆盖element-ui样式
		::v-deep .el-tree {
			.el-tree-node {
				.is-current {
					> .el-tree-node__content {
						background-color: #ecf5ff;
						color: #409eff;
					}
				}
				
				.el-tree-node__content {
					height: 36px;
					padding: 0 8px;
					font-size: 12px; /* 树形菜单文字大小调整为14px */
					
					&:hover {
						background-color: #f5f7fa;
					}
				}
			}
		}
		
		::v-deep .el-table {
			font-size: 14px; /* 表格文字大小调整为14px，与左侧树菜单一致 */
			
			th {
				padding: 8px 0;
			}
			
			.el-table__row {
				&:hover {
					td {
						background-color: #f5f7fa;
					}
				}
			}
			
			// 确保固定列不遮挡内容
			.el-table__fixed,
			.el-table__fixed-right {
				z-index: 2 !important;
			}
		}
		
		::v-deep .el-card {
			.el-card__body {
				display: flex;
				flex-direction: column;
				height: 100%;
				padding: 12px;
			}
		}
		
		::v-deep .el-input-group__append {
			background-color: #409eff;
			border-color: #409eff;
			color: #fff;
			
			&:hover {
				background-color: #66b1ff;
				border-color: #66b1ff;
			}
		}
		
		::v-deep .el-pagination {
			.el-pager {
				li {
					&.active {
						color: #409eff;
					}
				}
			}
		}
		
		::v-deep .el-dropdown-link {
			cursor: pointer;
			color: #409eff;
		}
		
		::v-deep .el-badge {
			.el-badge__content {
				font-size: 10px;
				padding: 0 4px;
			}
		}
		</style>